<template>
  <div>
    <Manpowermenubar_govem />

    <v-container fluid class="pa-4 pa-md-6 report-bg">
      <!-- ===== Header Card ===== -->
      <v-card class="mb-5 elevation-3 rounded-xl header-card">
        <v-card-title class="d-flex align-center py-4 px-6 header-title-bar">
          <div>
            <h1 class="text-h5 text-md-h4 font-weight-bold white--text">
              <v-icon left color="white" large>mdi-file-chart</v-icon>
              รายงานการเบิกจ่ายงบประมาณพนักงานราชการ
            </h1>
            <div class="white--text text--lighten-2 mt-1 ml-10 text-body-2">
              สรุปข้อมูลทุกงวด | ปีงบประมาณ {{ filters.report_year }} |
              รายงานส่ง สนผ. สอศ.
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            color="white"
            outlined
            class="mr-2"
            @click="exportFullReport"
            :loading="isExporting"
          >
            <v-icon left>mdi-microsoft-excel</v-icon> ส่งออก Excel
          </v-btn>
          <v-btn color="warning darken-2" dark @click="openSendToSNPDialog">
            <v-icon left>mdi-send</v-icon> ส่งรายงาน สนผ.
          </v-btn>
        </v-card-title>

        <!-- ===== Filter Section ===== -->
        <v-card-text class="px-6 py-5 filter-section">
          <div class="d-flex align-center mb-3">
            <v-icon color="primary" class="mr-2">mdi-filter-variant</v-icon>
            <span class="text-subtitle-1 font-weight-bold primary--text"
              >ตัวกรองข้อมูล</span
            >
            <v-spacer></v-spacer>
            <v-btn
              text
              small
              color="grey darken-1"
              @click="resetFilters"
              class="text-capitalize"
            >
              <v-icon small left>mdi-refresh</v-icon>
              ล้างตัวกรอง
            </v-btn>
          </div>

          <v-row dense class="mb-2">
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filters.report_year"
                :items="yearOptions"
                label="ปีงบประมาณ"
                outlined
                dense
                background-color="white"
                hide-details
                prepend-inner-icon="mdi-calendar"
                @change="fetchData"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filters.report_time"
                :items="timeOptionsWithAll"
                item-text="text"
                item-value="value"
                label="งวดการเบิกจ่าย"
                outlined
                dense
                background-color="white"
                hide-details
                prepend-inner-icon="mdi-clock-time-four-outline"
                @change="fetchData"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filters.status"
                :items="statusOptions"
                item-text="text"
                item-value="value"
                label="สถานะ"
                outlined
                dense
                background-color="white"
                hide-details
                prepend-inner-icon="mdi-check-circle-outline"
                clearable
                @change="fetchData"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.selectedUserStatuses"
                :items="userStatusOptions"
                item-text="text"
                item-value="value"
                label="กลุ่มสถานศึกษา"
                multiple
                chips
                small-chips
                deletable-chips
                outlined
                dense
                background-color="white"
                hide-details
                prepend-inner-icon="mdi-domain"
                placeholder="ทุกกลุ่ม"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="searchText"
                append-icon="mdi-magnify"
                label="ค้นหาสถานศึกษา/รหัส"
                outlined
                dense
                background-color="white"
                hide-details
                clearable
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- ===== Overall Summary Cards (เพิ่มอัตรากำลัง / อัตราว่าง) ===== -->
      <v-row class="mb-5">
        <v-col cols="12" sm="6" lg="3">
          <v-card
            class="rounded-xl elevation-2 summary-card summary-card--total"
          >
            <v-card-text class="pa-5">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="summary-icon-wrap summary-icon--indigo">
                  <v-icon color="white" size="28"
                    >mdi-file-document-multiple</v-icon
                  >
                </div>
                <span class="text-caption grey--text">รายงานทั้งหมด</span>
              </div>
              <div class="text-h4 font-weight-black mb-1">
                {{ overallSummary.totalReports }}
              </div>
              <div class="text-body-2 grey--text text--darken-1">
                จาก {{ overallSummary.totalColleges }} สถานศึกษา
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" lg="3">
          <v-card
            class="rounded-xl elevation-2 summary-card summary-card--allocated"
          >
            <v-card-text class="pa-5">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="summary-icon-wrap summary-icon--green">
                  <v-icon color="white" size="28">mdi-cash-plus</v-icon>
                </div>
                <span class="text-caption grey--text">งบโอนจัดสรรรวม</span>
              </div>
              <div class="text-h5 font-weight-black mb-1">
                {{ formatCurrencyShort(overallSummary.totalAllocated) }}
              </div>
              <div class="text-body-2 grey--text text--darken-1">
                {{ formatCurrency(overallSummary.totalAllocated) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" lg="3">
          <v-card
            class="rounded-xl elevation-2 summary-card summary-card--disbursed"
          >
            <v-card-text class="pa-5">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="summary-icon-wrap summary-icon--orange">
                  <v-icon color="white" size="28">mdi-cash-check</v-icon>
                </div>
                <v-progress-circular
                  :value="overallSummary.disbursePercentage"
                  :color="getPercentageColor(overallSummary.disbursePercentage)"
                  :size="42"
                  :width="5"
                  :rotate="-90"
                >
                  <span class="text-caption font-weight-bold"
                    >{{ overallSummary.disbursePercentage }}%</span
                  >
                </v-progress-circular>
              </div>
              <div class="text-h5 font-weight-black mb-1">
                {{ formatCurrencyShort(overallSummary.totalDisbursed) }}
              </div>
              <div class="text-body-2 grey--text text--darken-1">
                เบิกจ่ายรวมทุกงวด
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" lg="3">
          <v-card
            class="rounded-xl elevation-2 summary-card summary-card--remaining"
          >
            <v-card-text class="pa-5">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="summary-icon-wrap summary-icon--red">
                  <v-icon color="white" size="28">mdi-cash-minus</v-icon>
                </div>
                <span class="text-caption grey--text">เงินคงเหลือรวม</span>
              </div>
              <div class="text-h5 font-weight-black mb-1">
                {{ formatCurrencyShort(overallSummary.totalRemaining) }}
              </div>
              <div class="text-body-2 grey--text text--darken-1">
                {{ formatCurrency(overallSummary.totalRemaining) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- ===== Workforce Summary Cards (NEW - อัตรากำลัง) ===== -->
      <v-card
        class="rounded-xl elevation-2 mb-5"
        v-if="workforceSummary.totalPositions > 0"
      >
        <v-card-title class="py-3 px-5 workforce-header">
          <v-icon left color="white" small>mdi-account-group</v-icon>
          <span class="text-subtitle-1 font-weight-bold white--text">
            สรุปอัตรากำลังพนักงานราชการ
          </span>
          <v-spacer></v-spacer>
          <v-chip
            small
            dark
            color="white"
            text-color="info darken-3"
            class="font-weight-bold"
          >
            ข้อมูลจากรายงานที่ส่ง
          </v-chip>
        </v-card-title>
        <v-card-text class="pa-5">
          <v-row>
            <v-col cols="6" sm="4" md="2">
              <v-card
                outlined
                class="rounded-lg pa-3 text-center workforce-stat-card"
              >
                <v-icon color="primary" size="28" class="mb-1"
                  >mdi-account-multiple</v-icon
                >
                <div class="text-h5 font-weight-black primary--text">
                  {{ workforceSummary.totalPositions }}
                </div>
                <div class="text-caption grey--text" style="line-height: 1.2;">
                  กรอบอัตรากำลัง
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card
                outlined
                class="rounded-lg pa-3 text-center workforce-stat-card"
              >
                <v-icon color="success" size="28" class="mb-1"
                  >mdi-account-check</v-icon
                >
                <div class="text-h5 font-weight-black success--text">
                  {{ workforceSummary.filledPositions }}
                </div>
                <div class="text-caption grey--text" style="line-height: 1.2;">
                  มีคนครองตำแหน่ง
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card
                outlined
                class="rounded-lg pa-3 text-center workforce-stat-card workforce-stat-card--alert"
              >
                <v-icon color="error" size="28" class="mb-1"
                  >mdi-account-alert</v-icon
                >
                <div class="text-h5 font-weight-black error--text">
                  {{ workforceSummary.vacantPositions }}
                </div>
                <div class="text-caption grey--text" style="line-height: 1.2;">
                  อัตราว่าง
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card
                outlined
                class="rounded-lg pa-3 text-center workforce-stat-card"
              >
                <v-progress-circular
                  :rotate="-90"
                  :size="56"
                  :width="7"
                  :value="workforceSummary.filledPercentage"
                  :color="getPercentageColor(workforceSummary.filledPercentage)"
                  class="mb-1"
                >
                  <span class="text-body-2 font-weight-bold"
                    >{{ workforceSummary.filledPercentage }}%</span
                  >
                </v-progress-circular>
                <div
                  class="text-caption grey--text mt-1"
                  style="line-height: 1.2;"
                >
                  อัตราครองตำแหน่ง
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card
                outlined
                class="rounded-lg pa-3 text-center workforce-stat-card"
              >
                <v-icon color="info" size="28" class="mb-1"
                  >mdi-human-male</v-icon
                >
                <div class="text-h5 font-weight-black info--text">
                  {{ workforceSummary.maleCount }}
                </div>
                <div class="text-caption grey--text" style="line-height: 1.2;">
                  ชาย
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-card
                outlined
                class="rounded-lg pa-3 text-center workforce-stat-card"
              >
                <v-icon color="primary" size="28" class="mb-1"
                  >mdi-human-female</v-icon
                >
                <div class="text-h5 font-weight-black primary--text">
                  {{ workforceSummary.femaleCount }}
                </div>
                <div class="text-caption grey--text" style="line-height: 1.2;">
                  หญิง
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Vacancy by College Alert -->
          <v-slide-y-transition>
            <v-alert
              v-if="workforceSummary.vacantPositions > 0"
              type="warning"
              text
              dense
              border="left"
              class="mt-4 mb-0"
            >
              <div class="d-flex align-center flex-wrap">
                <v-icon small color="warning" class="mr-2"
                  >mdi-alert-circle</v-icon
                >
                <span class="font-weight-bold mr-2">
                  {{ workforceSummary.vacantCollegeCount }} สถานศึกษา
                  มีอัตราว่างรวม {{ workforceSummary.vacantPositions }} อัตรา
                </span>
                <v-chip
                  v-for="vc in workforceSummary.topVacantColleges.slice(0, 5)"
                  :key="vc.college_code"
                  x-small
                  color="error lighten-4"
                  text-color="error darken-3"
                  class="mr-1 mb-1"
                  label
                >
                  {{ vc.college_name }} ({{ vc.vacant }})
                </v-chip>
                <span
                  v-if="workforceSummary.topVacantColleges.length > 5"
                  class="text-caption grey--text ml-1"
                >
                  +{{ workforceSummary.topVacantColleges.length - 5 }} แห่ง
                </span>
              </div>
            </v-alert>
          </v-slide-y-transition>
        </v-card-text>
      </v-card>

      <!-- ===== Period Summary Section ===== -->
      <v-card class="rounded-xl elevation-2 mb-5 period-summary-card">
        <v-card-title class="py-4 px-5 period-summary-header">
          <v-icon left color="white" size="24">mdi-calendar-clock</v-icon>
          <span class="text-subtitle-1 font-weight-bold white--text">
            สรุปการเบิกจ่ายตามงวด — ปีงบประมาณ {{ filters.report_year }}
          </span>
          <v-spacer></v-spacer>
          <v-chip
            small
            dark
            color="white"
            text-color="primary darken-3"
            class="font-weight-bold"
          >
            {{ activePeriods.length }} งวดที่มีข้อมูล
          </v-chip>
        </v-card-title>

        <v-card-text class="pa-5" v-if="activePeriods.length > 0">
          <!-- Period Tabs -->
          <v-tabs
            v-model="selectedPeriodTab"
            background-color="transparent"
            color="primary darken-1"
            class="mb-4 period-tabs"
            show-arrows
          >
            <v-tab key="all" class="text-capitalize">
              <v-icon left small>mdi-view-dashboard</v-icon>
              ภาพรวมทุกงวด
            </v-tab>
            <v-tab
              v-for="p in activePeriods"
              :key="'tab-' + p.report_time"
              class="text-capitalize"
            >
              <v-icon left small>mdi-numeric-{{ p.report_time }}-circle</v-icon>
              งวดที่ {{ p.report_time }}
              <v-chip
                x-small
                class="ml-1"
                :color="
                  p.disburse_pct >= 80
                    ? 'success'
                    : p.disburse_pct >= 50
                    ? 'warning'
                    : 'error'
                "
                dark
              >
                {{ p.disburse_pct }}%
              </v-chip>
            </v-tab>
          </v-tabs>

          <!-- Tab Content -->
          <v-tabs-items v-model="selectedPeriodTab">
            <!-- All Periods Overview -->
            <v-tab-item key="all">
              <v-row>
                <v-col
                  v-for="p in allPeriodsDisplay"
                  :key="'period-' + p.report_time"
                  cols="12"
                  md="6"
                  lg="4"
                >
                  <v-card
                    outlined
                    class="rounded-lg period-detail-card"
                    :class="{
                      'period-detail-card--active': p.hasData,
                      'period-detail-card--inactive': !p.hasData
                    }"
                  >
                    <div class="period-detail-header pa-4 pb-2">
                      <div class="d-flex align-center justify-space-between">
                        <div>
                          <div
                            class="text-h6 font-weight-bold"
                            :class="
                              p.hasData
                                ? 'primary--text text--darken-2'
                                : 'grey--text'
                            "
                          >
                            งวดที่ {{ p.report_time }}
                          </div>
                          <div
                            class="text-caption"
                            :class="
                              p.hasData
                                ? 'grey--text text--darken-1'
                                : 'grey--text'
                            "
                          >
                            {{ p.period_label }} | {{ p.months_label }}
                          </div>
                        </div>
                        <v-chip
                          v-if="p.hasData"
                          small
                          :color="
                            p.disburse_pct >= 80
                              ? 'success'
                              : p.disburse_pct >= 50
                              ? 'warning'
                              : 'error'
                          "
                          dark
                          class="font-weight-bold"
                        >
                          {{ p.disburse_pct }}%
                        </v-chip>
                        <v-chip
                          v-else-if="p.isOpen"
                          small
                          color="success lighten-4"
                          text-color="success darken-2"
                        >
                          <v-icon left x-small
                            >mdi-lock-open-variant-outline</v-icon
                          >
                          เปิดรับรายงาน
                        </v-chip>
                        <v-chip
                          v-else
                          small
                          color="grey lighten-2"
                          text-color="grey"
                        >
                          <v-icon left x-small>mdi-lock-outline</v-icon>
                          ยังไม่เปิด
                        </v-chip>
                      </div>
                      <v-progress-linear
                        v-if="p.hasData"
                        :value="Math.min(p.disburse_pct, 100)"
                        :color="getPercentageColor(p.disburse_pct)"
                        height="8"
                        rounded
                        class="mt-3"
                        background-color="grey lighten-3"
                      ></v-progress-linear>
                    </div>

                    <v-divider></v-divider>

                    <div class="pa-4" v-if="p.hasData">
                      <div class="budget-row mb-3">
                        <div class="d-flex align-center mb-1">
                          <v-icon x-small color="success darken-1" class="mr-1"
                            >mdi-arrow-down-circle</v-icon
                          >
                          <span class="text-caption grey--text text--darken-1"
                            >เงินโอนจัดสรร</span
                          >
                        </div>
                        <div
                          class="text-subtitle-1 font-weight-bold success--text text--darken-2"
                        >
                          {{ formatCurrency(p.total_allocated) }}
                        </div>
                      </div>
                      <div class="budget-row mb-3">
                        <div class="d-flex align-center mb-1">
                          <v-icon x-small color="warning darken-1" class="mr-1"
                            >mdi-arrow-up-circle</v-icon
                          >
                          <span class="text-caption grey--text text--darken-1"
                            >เบิกจ่ายแล้ว</span
                          >
                        </div>
                        <div
                          class="text-subtitle-1 font-weight-bold warning--text text--darken-2"
                        >
                          {{ formatCurrency(p.total_disbursed) }}
                        </div>
                      </div>
                      <div class="budget-row">
                        <div class="d-flex align-center mb-1">
                          <v-icon x-small color="info darken-1" class="mr-1"
                            >mdi-wallet-outline</v-icon
                          >
                          <span class="text-caption grey--text text--darken-1"
                            >เงินคงเหลือ</span
                          >
                        </div>
                        <div
                          class="text-subtitle-1 font-weight-bold"
                          :class="
                            p.total_remaining < 0
                              ? 'red--text'
                              : 'blue--text text--darken-2'
                          "
                        >
                          {{ formatCurrency(p.total_remaining) }}
                        </div>
                      </div>

                      <v-divider class="my-3"></v-divider>

                      <!-- Workforce info in period card -->
                      <div
                        class="d-flex justify-space-between text-caption grey--text mb-2"
                      >
                        <span>
                          <v-icon x-small class="mr-1"
                            >mdi-file-document</v-icon
                          >
                          {{ p.count }} รายงาน
                        </span>
                        <span>
                          <v-icon x-small class="mr-1">mdi-domain</v-icon>
                          {{ p.college_count }} สถานศึกษา
                        </span>
                      </div>
                      <div class="d-flex justify-space-between text-caption">
                        <span class="primary--text font-weight-medium">
                          <v-icon x-small color="primary" class="mr-1"
                            >mdi-account-multiple</v-icon
                          >
                          กรอบอัตรา {{ p.total_positions || 0 }}
                        </span>
                        <span class="success--text font-weight-medium">
                          <v-icon x-small color="success" class="mr-1"
                            >mdi-account-check</v-icon
                          >
                          ครอง {{ p.filled_positions || 0 }}
                        </span>
                        <span class="error--text font-weight-medium">
                          <v-icon x-small color="error" class="mr-1"
                            >mdi-account-alert</v-icon
                          >
                          ว่าง {{ p.vacant_positions || 0 }}
                        </span>
                      </div>
                    </div>

                    <div class="pa-4 text-center" v-else>
                      <template v-if="p.isOpen">
                        <v-icon color="success lighten-1" size="40"
                          >mdi-lock-open-variant-outline</v-icon
                        >
                        <div
                          class="text-caption success--text text--darken-1 mt-2 font-weight-bold"
                        >
                          เปิดรับรายงาน
                        </div>
                        <div
                          class="text-caption grey--text mt-1"
                          v-if="p.closeDate"
                        >
                          ถึง {{ formatDateThai(p.closeDate) }}
                        </div>
                        <div class="text-caption grey--text">
                          ยังไม่มีสถานศึกษาส่งรายงาน
                        </div>
                      </template>
                      <template v-else>
                        <v-icon color="grey lighten-1" size="40"
                          >mdi-lock-outline</v-icon
                        >
                        <div class="text-caption grey--text mt-2">
                          ยังไม่เปิดให้รายงาน
                        </div>
                        <div
                          class="text-caption grey--text text--lighten-1 mt-1"
                          v-if="p.openDate"
                        >
                          กำหนดเปิด {{ formatDateThai(p.openDate) }}
                        </div>
                      </template>
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Cumulative Summary -->
              <v-card class="rounded-lg mt-4 cumulative-card" outlined>
                <v-card-text class="pa-4">
                  <div class="d-flex align-center mb-3">
                    <v-icon small color="primary" class="mr-2"
                      >mdi-sigma</v-icon
                    >
                    <span
                      class="text-subtitle-2 font-weight-bold primary--text"
                    >
                      สรุปยอดสะสม งวดที่ 1–{{ lastActivePeriod }}
                    </span>
                  </div>
                  <v-row dense>
                    <v-col cols="12" sm="4">
                      <div
                        class="text-center pa-3 rounded-lg"
                        style="background: #e8f5e9;"
                      >
                        <div
                          class="text-caption success--text text--darken-2 mb-1"
                        >
                          รวมเงินโอนจัดสรร
                        </div>
                        <div
                          class="text-h6 font-weight-bold success--text text--darken-3"
                        >
                          {{ formatCurrency(overallSummary.totalAllocated) }}
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div
                        class="text-center pa-3 rounded-lg"
                        style="background: #fff4dc;"
                      >
                        <div
                          class="text-caption warning--text text--darken-2 mb-1"
                        >
                          รวมเบิกจ่าย
                        </div>
                        <div
                          class="text-h6 font-weight-bold warning--text text--darken-3"
                        >
                          {{ formatCurrency(overallSummary.totalDisbursed) }}
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div
                        class="text-center pa-3 rounded-lg"
                        :style="
                          overallSummary.totalRemaining < 0
                            ? 'background: #ffebee;'
                            : 'background: #f5f5f5;'
                        "
                      >
                        <div
                          class="text-caption mb-1"
                          :class="
                            overallSummary.totalRemaining < 0
                              ? 'red--text text--darken-2'
                              : 'blue--text text--darken-2'
                          "
                        >
                          รวมเงินคงเหลือ
                        </div>
                        <div
                          class="text-h6 font-weight-bold"
                          :class="
                            overallSummary.totalRemaining < 0
                              ? 'red--text text--darken-3'
                              : 'blue--text text--darken-3'
                          "
                        >
                          {{ formatCurrency(overallSummary.totalRemaining) }}
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Individual Period Detail -->
            <v-tab-item
              v-for="p in activePeriods"
              :key="'content-' + p.report_time"
            >
              <v-row class="mb-4">
                <v-col cols="12" md="3">
                  <v-card
                    outlined
                    class="rounded-lg pa-4 text-center budget-highlight-card budget-highlight--allocated"
                  >
                    <v-icon color="success darken-1" size="32" class="mb-2"
                      >mdi-arrow-down-circle</v-icon
                    >
                    <div class="text-caption grey--text mb-1">
                      เงินโอนจัดสรร
                    </div>
                    <div
                      class="text-h6 font-weight-bold success--text text--darken-2"
                    >
                      {{ formatCurrency(p.total_allocated) }}
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card
                    outlined
                    class="rounded-lg pa-4 text-center budget-highlight-card budget-highlight--disbursed"
                  >
                    <v-icon color="warning darken-1" size="32" class="mb-2"
                      >mdi-arrow-up-circle</v-icon
                    >
                    <div class="text-caption grey--text mb-1">เบิกจ่ายแล้ว</div>
                    <div
                      class="text-h6 font-weight-bold warning--text text--darken-2"
                    >
                      {{ formatCurrency(p.total_disbursed) }}
                    </div>
                    <v-chip
                      x-small
                      :color="getPercentageColor(p.disburse_pct)"
                      dark
                      class="mt-1"
                      >{{ p.disburse_pct }}%</v-chip
                    >
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card
                    outlined
                    class="rounded-lg pa-4 text-center budget-highlight-card budget-highlight--remaining"
                  >
                    <v-icon
                      :color="
                        p.total_remaining < 0 ? 'error darken-1' : 'info darken-1'
                      "
                      size="32"
                      class="mb-2"
                      >mdi-wallet-outline</v-icon
                    >
                    <div class="text-caption grey--text mb-1">เงินคงเหลือ</div>
                    <div
                      class="text-h6 font-weight-bold"
                      :class="
                        p.total_remaining < 0
                          ? 'red--text text--darken-2'
                          : 'blue--text text--darken-2'
                      "
                    >
                      {{ formatCurrency(p.total_remaining) }}
                    </div>
                  </v-card>
                </v-col>
                <!-- Workforce card per period -->
                <v-col cols="12" md="3">
                  <v-card
                    outlined
                    class="rounded-lg pa-4 text-center budget-highlight-card budget-highlight--workforce"
                  >
                    <v-icon color="primary darken-1" size="32" class="mb-2"
                      >mdi-account-group</v-icon
                    >
                    <div class="text-caption grey--text mb-1">อัตรากำลัง</div>
                    <div
                      class="d-flex justify-center align-center"
                      style="gap: 12px;"
                    >
                      <div>
                        <div class="text-h6 font-weight-bold primary--text">
                          {{ p.total_positions || 0 }}
                        </div>
                        <div class="text-caption grey--text">กรอบ</div>
                      </div>
                      <v-divider vertical></v-divider>
                      <div>
                        <div class="text-h6 font-weight-bold success--text">
                          {{ p.filled_positions || 0 }}
                        </div>
                        <div class="text-caption grey--text">ครอง</div>
                      </div>
                      <v-divider vertical></v-divider>
                      <div>
                        <div class="text-h6 font-weight-bold error--text">
                          {{ p.vacant_positions || 0 }}
                        </div>
                        <div class="text-caption grey--text">ว่าง</div>
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Monthly Breakdown -->
              <v-card outlined class="rounded-lg mb-4">
                <v-card-title class="py-3 px-4">
                  <v-icon left small color="info">mdi-calendar-month</v-icon>
                  <span class="text-subtitle-2 font-weight-bold">
                    รายละเอียดรายเดือน — {{ p.months_label }}
                  </span>
                </v-card-title>
                <v-simple-table dense class="period-monthly-table">
                  <thead>
                    <tr class="grey lighten-4">
                      <th class="text-left">เดือน</th>
                      <th class="text-right">เงินโอนจัดสรร</th>
                      <th class="text-right">เบิกจ่าย</th>
                      <th class="text-right">คงเหลือ</th>
                      <th class="text-center" style="width: 120px;">
                        อัตราเบิกจ่าย
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="m in getPeriodMonthlyBreakdown(p.report_time)"
                      :key="'m-' + m.month"
                    >
                      <td>
                        <v-icon x-small color="info" class="mr-1"
                          >mdi-circle</v-icon
                        >
                        {{ getMonthName(m.month) }}
                      </td>
                      <td class="text-right font-weight-medium">
                        {{ formatCurrency(m.total_allocated) }}
                      </td>
                      <td class="text-right font-weight-medium">
                        {{ formatCurrency(m.total_disbursed) }}
                      </td>
                      <td
                        class="text-right font-weight-medium"
                        :class="{ 'red--text': m.total_remaining < 0 }"
                      >
                        {{ formatCurrency(m.total_remaining) }}
                      </td>
                      <td>
                        <v-progress-linear
                          :value="Math.min(m.disburse_pct, 100)"
                          :color="getPercentageColor(m.disburse_pct)"
                          height="18"
                          rounded
                        >
                          <template v-slot:default>
                            <strong class="white--text text-caption"
                              >{{ m.disburse_pct }}%</strong
                            >
                          </template>
                        </v-progress-linear>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="font-weight-bold" style="background: #f5f5f5;">
                      <td>
                        <v-icon x-small color="primary" class="mr-1"
                          >mdi-sigma</v-icon
                        >
                        รวมงวดที่ {{ p.report_time }}
                      </td>
                      <td class="text-right success--text text--darken-2">
                        {{ formatCurrency(p.total_allocated) }}
                      </td>
                      <td class="text-right warning--text text--darken-2">
                        {{ formatCurrency(p.total_disbursed) }}
                      </td>
                      <td
                        class="text-right"
                        :class="
                          p.total_remaining < 0
                            ? 'red--text'
                            : 'blue--text text--darken-2'
                        "
                      >
                        {{ formatCurrency(p.total_remaining) }}
                      </td>
                      <td>
                        <v-chip
                          small
                          :color="getPercentageColor(p.disburse_pct)"
                          dark
                          class="font-weight-bold"
                        >
                          {{ p.disburse_pct }}%
                        </v-chip>
                      </td>
                    </tr>
                  </tfoot>
                </v-simple-table>
              </v-card>

              <!-- College-level Detail with Workforce Columns -->
              <v-card outlined class="rounded-lg">
                <v-card-title class="py-3 px-4 d-flex align-center">
                  <v-icon left small color="primary">mdi-domain</v-icon>
                  <span class="text-subtitle-2 font-weight-bold">
                    รายสถานศึกษา — งวดที่ {{ p.report_time }}
                  </span>
                  <v-chip small class="ml-2" color="primary" dark
                    >{{ getPeriodReports(p.report_time).length }} แห่ง</v-chip
                  >
                  <v-spacer></v-spacer>
                  <v-btn
                    x-small
                    color="success"
                    outlined
                    @click="exportPeriodData(p.report_time)"
                  >
                    <v-icon x-small left>mdi-microsoft-excel</v-icon> ส่งออก
                  </v-btn>
                </v-card-title>
                <v-data-table
                  :headers="periodCollegeHeaders"
                  :items="getPeriodReports(p.report_time)"
                  :search="searchText"
                  :items-per-page="10"
                  :loading="isLoading"
                  dense
                  class="elevation-0"
                  :footer-props="{
                    'items-per-page-options': [10, 20, 50, -1],
                    'items-per-page-text': 'แสดง'
                  }"
                >
                  <template v-slot:item.user_status="{ item }">
                    <v-chip
                      x-small
                      label
                      :color="getUserStatusColor(item.user_status_raw)"
                      dark
                    >
                      {{ item.user_status }}
                    </v-chip>
                  </template>

                  <template v-slot:item.organization_name="{ item }">
                    <div class="font-weight-bold text-body-2">
                      {{ item.organization_name }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ item.province_name }}
                    </div>
                  </template>

                  <template v-slot:item.status="{ item }">
                    <v-chip
                      x-small
                      :color="getStatusColor(item.status)"
                      dark
                      label
                    >
                      <v-icon left x-small>{{
                        getStatusIcon(item.status)
                      }}</v-icon>
                      {{ getStatusText(item.status) }}
                    </v-chip>
                  </template>

                  <!-- กรอบอัตรากำลัง -->
                  <template v-slot:item.count_manpower="{ item }">
                    <span class="font-weight-bold primary--text">{{
                      item.count_manpower  || 0
                    }}</span>
                  </template>

                  <!-- มีคนครอง -->
                  <template v-slot:item.filled_positions="{ item }">
                    <v-chip x-small color="success" text-color="white" label>
                      {{ item.filled_positions || item.total_employees || 0 }}
                    </v-chip>
                  </template>

                  <!-- อัตราว่าง -->
                  <template v-slot:item.vacant_positions="{ item }">
                    <v-chip
                      x-small
                      :color="
                        (item.vacant_positions || 0) > 0
                          ? 'error'
                          : 'grey lighten-2'
                      "
                      :text-color="
                        (item.vacant_positions || 0) > 0 ? 'white' : 'grey'
                      "
                      label
                    >
                      <v-icon
                        x-small
                        left
                        v-if="(item.vacant_positions || 0) > 0"
                        >mdi-alert</v-icon
                      >
                      {{ item.vacant_positions || 0 }}
                    </v-chip>
                  </template>

                  <template v-slot:item.allocated_budget="{ item }">
                    <span class="font-weight-medium">{{
                      formatCurrency(item.allocated_budget)
                    }}</span>
                  </template>

                  <template v-slot:item.disbursed_budget="{ item }">
                    <span class="font-weight-medium">{{
                      formatCurrency(item.disbursed_budget)
                    }}</span>
                  </template>

                  <template v-slot:item.remaining_budget="{ item }">
                    <span
                      class="font-weight-medium"
                      :class="{ 'red--text': item.remaining_budget < 0 }"
                    >
                      {{ formatCurrency(item.remaining_budget) }}
                    </span>
                  </template>

                  <template v-slot:item.disburse_pct="{ item }">
                    <v-progress-linear
                      :value="Math.min(item.disburse_pct, 100)"
                      :color="getPercentageColor(item.disburse_pct)"
                      height="18"
                      rounded
                    >
                      <template v-slot:default>
                        <strong class="white--text text-caption"
                          >{{ item.disburse_pct }}%</strong
                        >
                      </template>
                    </v-progress-linear>
                  </template>

                  <template v-slot:item.letterFile="{ item }">
                    <v-btn
                      v-if="item.letterFile"
                      icon
                      x-small
                      color="primary"
                      @click="openFileViewer(item.letterFile)"
                      title="ดูหนังสือนำส่ง"
                    >
                      <v-icon small>mdi-file-document-outline</v-icon>
                    </v-btn>
                    <span v-else class="grey--text">-</span>
                  </template>

                  <template v-slot:item.report_file="{ item }">
                    <v-btn
                      v-if="item.report_file"
                      icon
                      x-small
                      color="success"
                      @click="openFileViewer(item.report_file)"
                      title="ดูไฟล์รายงาน"
                    >
                      <v-icon small>mdi-file-chart-outline</v-icon>
                    </v-btn>
                    <span v-else class="grey--text">-</span>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      icon
                      x-small
                      color="primary"
                      @click="viewDetail(item)"
                      title="ดูรายละเอียด"
                    >
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>

        <v-card-text v-else class="text-center pa-10">
          <v-icon size="64" color="grey lighten-2"
            >mdi-file-search-outline</v-icon
          >
          <div class="text-h6 grey--text mt-3">ไม่พบข้อมูลรายงาน</div>
          <div class="text-body-2 grey--text">
            กรุณาเลือกปีงบประมาณหรือเปลี่ยนตัวกรอง
          </div>
        </v-card-text>
      </v-card>

      <!-- ===== Cross-Tab Pivot Table (เพิ่มอัตรากำลัง/อัตราว่าง) ===== -->
      <v-card
        class="rounded-xl elevation-2 overflow-hidden mt-5"
        :loading="isLoading"
      >
        <v-card-title class="flex-wrap py-3 pivot-table-header">
          <v-icon left color="primary">mdi-table-large</v-icon>
          รายละเอียดการเบิกจ่ายรายสถานศึกษา (ทุกงวด)
          <v-chip small class="ml-2" color="primary" dark>
            {{ pivotData.length }} สถานศึกษา
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn-toggle
            v-model="pivotDisplayMode"
            mandatory
            dense
            color="primary"
            class="mr-2"
          >
            <v-btn small value="allocated">
              <v-icon small left>mdi-cash-plus</v-icon> จัดสรร
            </v-btn>
            <v-btn small value="disbursed">
              <v-icon small left>mdi-cash-check</v-icon> เบิกจ่าย
            </v-btn>
            <v-btn small value="remaining">
              <v-icon small left>mdi-cash-minus</v-icon> คงเหลือ
            </v-btn>
            <v-btn small value="all">
              <v-icon small left>mdi-view-grid</v-icon> ทั้งหมด
            </v-btn>
          </v-btn-toggle>
          <v-btn
            color="success"
            small
            class="ml-2"
            @click="exportPivotData"
            :loading="isExportingFiltered"
          >
            <v-icon small left>mdi-microsoft-excel</v-icon>
            ส่งออก
          </v-btn>
        </v-card-title>

        <div class="pivot-table-wrapper">
          <v-simple-table dense class="pivot-cross-table">
            <thead>
              <tr class="pivot-header-row-1">
                <th
                  rowspan="2"
                  class="pivot-fixed-col pivot-th-info text-center"
                  style="min-width:50px;"
                >
                  ลำดับ
                </th>
                <th
                  rowspan="2"
                  class="pivot-fixed-col pivot-th-info text-left"
                  style="min-width:70px;"
                >
                  กลุ่ม
                </th>
                <th
                  rowspan="2"
                  class="pivot-fixed-col pivot-th-info text-left"
                  style="min-width:90px;"
                >
                  รหัส
                </th>
                <th
                  rowspan="2"
                  class="pivot-fixed-col pivot-th-info text-left"
                  style="min-width:100px;"
                >
                  รหัสหน่วยงาน
                </th>
                <th
                  rowspan="2"
                  class="pivot-fixed-col pivot-th-info text-left"
                  style="min-width:100px;"
                >
                  รหัสหน่วยเบิก
                </th>
                <th
                  rowspan="2"
                  class="pivot-fixed-col pivot-th-info text-left"
                  style="min-width:180px;"
                >
                  สถานศึกษา
                </th>
                <!-- NEW: Workforce columns -->
                <th
                  rowspan="2"
                  class="pivot-th-workforce text-center"
                  style="min-width:60px;"
                >
                  กรอบอัตรา
                </th>
                <th
                  rowspan="2"
                  class="pivot-th-workforce text-center"
                  style="min-width:60px;"
                >
                  มีคนครอง
                </th>
                <th
                  rowspan="2"
                  class="pivot-th-workforce pivot-th-workforce--alert text-center"
                  style="min-width:60px;"
                >
                  อัตราว่าง
                </th>
                <template v-for="pt in activePeriodsNumbers">
                  <th
                    :key="'ph-' + pt"
                    :colspan="pivotDisplayMode === 'all' ? 3 : 1"
                    class="pivot-th-period text-center"
                    :class="'pivot-period-' + pt"
                  >
                    งวด {{ pt }}
                    <div
                      class="text-caption font-weight-regular"
                      style="opacity:0.85;"
                    >
                      {{ getPeriodLabel(pt) }}
                    </div>
                  </th>
                </template>
                <th
                  :colspan="pivotDisplayMode === 'all' ? 3 : 1"
                  class="pivot-th-total text-center"
                  v-if="activePeriodsNumbers.length > 1"
                >
                  รวมทุกงวด
                </th>
                <th
                  rowspan="2"
                  class="pivot-th-info text-center"
                  style="min-width:50px;"
                ></th>
              </tr>
              <tr class="pivot-header-row-2">
                <template v-for="pt in activePeriodsNumbers">
                  <template v-if="pivotDisplayMode === 'all'">
                    <th
                      :key="'sub-a-' + pt"
                      class="pivot-sub-th text-right"
                      :class="'pivot-period-' + pt + '-sub'"
                    >
                      จัดสรร
                    </th>
                    <th
                      :key="'sub-d-' + pt"
                      class="pivot-sub-th text-right"
                      :class="'pivot-period-' + pt + '-sub'"
                    >
                      เบิกจ่าย
                    </th>
                    <th
                      :key="'sub-r-' + pt"
                      class="pivot-sub-th text-right"
                      :class="'pivot-period-' + pt + '-sub'"
                    >
                      คงเหลือ
                    </th>
                  </template>
                  <template v-else>
                    <th
                      :key="'sub-s-' + pt"
                      class="pivot-sub-th text-right"
                      :class="'pivot-period-' + pt + '-sub'"
                    >
                      {{
                        pivotDisplayMode === "allocated"
                          ? "จัดสรร"
                          : pivotDisplayMode === "disbursed"
                          ? "เบิกจ่าย"
                          : "คงเหลือ"
                      }}
                    </th>
                  </template>
                </template>
                <template v-if="activePeriodsNumbers.length > 1">
                  <template v-if="pivotDisplayMode === 'all'">
                    <th class="pivot-sub-th pivot-total-sub text-right">
                      จัดสรร
                    </th>
                    <th class="pivot-sub-th pivot-total-sub text-right">
                      เบิกจ่าย
                    </th>
                    <th class="pivot-sub-th pivot-total-sub text-right">
                      คงเหลือ
                    </th>
                  </template>
                  <template v-else>
                    <th class="pivot-sub-th pivot-total-sub text-right">
                      {{
                        pivotDisplayMode === "allocated"
                          ? "รวมจัดสรร"
                          : pivotDisplayMode === "disbursed"
                          ? "รวมเบิกจ่าย"
                          : "รวมคงเหลือ"
                      }}
                    </th>
                  </template>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in filteredPivotData"
                :key="'pivot-' + row.college_code"
                class="pivot-data-row"
                :class="{ 'pivot-row-alt': idx % 2 === 1 }"
              >
                <td class="text-center text-caption grey--text">
                  {{ pivotPageStart + idx + 1 }}
                </td>
                <td>
                  <v-chip
                    x-small
                    label
                    :color="getUserStatusColor(row.user_status_raw)"
                    dark
                  >
                    {{ row.user_status }}
                  </v-chip>
                </td>
                <td class="text-body-2">{{ row.college_code }}</td>
                <td class="text-body-2">{{ row.organization_code }}</td>
                <td class="text-body-2">{{ row.allocation_code }}</td>
                <td>
                  <div class="font-weight-bold text-body-2">
                    {{ row.organization_name }}
                  </div>
                  <div class="text-caption grey--text">
                    {{ row.province_name }}
                  </div>
                </td>
                <!-- NEW: Workforce data cells -->
                <td class="text-center font-weight-bold primary--text">
                  {{ row.total_positions || row.total_employees || 0 }}
                </td>
                <td class="text-center">
                  <v-chip x-small color="success" text-color="white" label>{{
                    row.filled_positions || row.total_employees || 0
                  }}</v-chip>
                </td>
                <td class="text-center">
                  <v-chip
                    x-small
                    :color="
                      (row.vacant_positions || 0) > 0
                        ? 'error'
                        : 'grey lighten-2'
                    "
                    :text-color="
                      (row.vacant_positions || 0) > 0
                        ? 'white'
                        : 'grey darken-1'
                    "
                    label
                  >
                    <v-icon x-small left v-if="(row.vacant_positions || 0) > 0"
                      >mdi-alert</v-icon
                    >
                    {{ row.vacant_positions || 0 }}
                  </v-chip>
                </td>

                <!-- Period Data Cells -->
                <template v-for="pt in activePeriodsNumbers">
                  <template v-if="pivotDisplayMode === 'all'">
                    <td
                      :key="'ca-' + row.college_code + '-' + pt"
                      class="text-right text-body-2 pivot-cell-allocated"
                    >
                      <template v-if="row.periods[pt]">{{
                        formatCurrency(row.periods[pt].allocated)
                      }}</template>
                      <span v-else class="grey--text text--lighten-1">-</span>
                    </td>
                    <td
                      :key="'cd-' + row.college_code + '-' + pt"
                      class="text-right text-body-2 pivot-cell-disbursed"
                    >
                      <template v-if="row.periods[pt]">{{
                        formatCurrency(row.periods[pt].disbursed)
                      }}</template>
                      <span v-else class="grey--text text--lighten-1">-</span>
                    </td>
                    <td
                      :key="'cr-' + row.college_code + '-' + pt"
                      class="text-right text-body-2 pivot-cell-remaining"
                    >
                      <template v-if="row.periods[pt]">
                        <span
                          :class="{
                            'red--text': row.periods[pt].remaining < 0
                          }"
                        >
                          {{ formatCurrency(row.periods[pt].remaining) }}
                        </span>
                      </template>
                      <span v-else class="grey--text text--lighten-1">-</span>
                    </td>
                  </template>
                  <template v-else>
                    <td
                      :key="'cs-' + row.college_code + '-' + pt"
                      class="text-right text-body-2"
                    >
                      <template v-if="row.periods[pt]">
                        <span
                          :class="{
                            'red--text':
                              pivotDisplayMode === 'remaining' &&
                              row.periods[pt].remaining < 0,
                            'green--text text--darken-2':
                              pivotDisplayMode === 'allocated',
                            'orange--text text--darken-2':
                              pivotDisplayMode === 'disbursed'
                          }"
                        >
                          {{
                            formatCurrency(
                              row.periods[pt][
                                pivotDisplayMode === "allocated"
                                  ? "allocated"
                                  : pivotDisplayMode === "disbursed"
                                  ? "disbursed"
                                  : "remaining"
                              ]
                            )
                          }}
                        </span>
                      </template>
                      <span v-else class="grey--text text--lighten-1">-</span>
                    </td>
                  </template>
                </template>

                <!-- Total -->
                <template v-if="activePeriodsNumbers.length > 1">
                  <template v-if="pivotDisplayMode === 'all'">
                    <td
                      class="text-right font-weight-bold text-body-2 pivot-cell-total success--text text--darken-2"
                    >
                      {{ formatCurrency(row.total_allocated) }}
                    </td>
                    <td
                      class="text-right font-weight-bold text-body-2 pivot-cell-total warning--text text--darken-2"
                    >
                      {{ formatCurrency(row.total_disbursed) }}
                    </td>
                    <td
                      class="text-right font-weight-bold text-body-2 pivot-cell-total"
                      :class="
                        row.total_remaining < 0
                          ? 'red--text'
                          : 'blue--text text--darken-2'
                      "
                    >
                      {{ formatCurrency(row.total_remaining) }}
                    </td>
                  </template>
                  <template v-else>
                    <td
                      class="text-right font-weight-bold text-body-2 pivot-cell-total"
                    >
                      <span
                        :class="{
                          'green--text text--darken-2':
                            pivotDisplayMode === 'allocated',
                          'orange--text text--darken-2':
                            pivotDisplayMode === 'disbursed',
                          'red--text':
                            pivotDisplayMode === 'remaining' &&
                            row.total_remaining < 0,
                          'blue--text text--darken-2':
                            pivotDisplayMode === 'remaining' &&
                            row.total_remaining >= 0
                        }"
                      >
                        {{
                          formatCurrency(
                            pivotDisplayMode === "allocated"
                              ? row.total_allocated
                              : pivotDisplayMode === "disbursed"
                              ? row.total_disbursed
                              : row.total_remaining
                          )
                        }}
                      </span>
                    </td>
                  </template>
                </template>

                <td class="text-center">
                  <v-btn
                    icon
                    x-small
                    color="primary"
                    @click="viewCollegeAllPeriods(row)"
                    title="ดูรายละเอียด"
                  >
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="pivot-footer-row">
                <td colspan="6" class="text-right font-weight-bold text-body-2">
                  <v-icon small class="mr-1">mdi-sigma</v-icon>
                  รวมทั้งหมด
                </td>
                <!-- Workforce totals -->
                <td class="text-center font-weight-black primary--text">
                  {{ pivotGrandTotal.total_positions }}
                </td>
                <td class="text-center font-weight-black success--text">
                  {{ pivotGrandTotal.filled_positions }}
                </td>
                <td class="text-center font-weight-black error--text">
                  {{ pivotGrandTotal.vacant_positions }}
                </td>

                <template v-for="pt in activePeriodsNumbers">
                  <template v-if="pivotDisplayMode === 'all'">
                    <td
                      :key="'fa-' + pt"
                      class="text-right font-weight-bold success--text text--darken-2"
                    >
                      {{
                        formatCurrency(
                          pivotGrandTotal.periods[pt]
                            ? pivotGrandTotal.periods[pt].allocated
                            : 0
                        )
                      }}
                    </td>
                    <td
                      :key="'fd-' + pt"
                      class="text-right font-weight-bold warning--text text--darken-2"
                    >
                      {{
                        formatCurrency(
                          pivotGrandTotal.periods[pt]
                            ? pivotGrandTotal.periods[pt].disbursed
                            : 0
                        )
                      }}
                    </td>
                    <td
                      :key="'fr-' + pt"
                      class="text-right font-weight-bold"
                      :class="
                        (pivotGrandTotal.periods[pt]
                          ? pivotGrandTotal.periods[pt].remaining
                          : 0) < 0
                          ? 'red--text'
                          : 'blue--text text--darken-2'
                      "
                    >
                      {{
                        formatCurrency(
                          pivotGrandTotal.periods[pt]
                            ? pivotGrandTotal.periods[pt].remaining
                            : 0
                        )
                      }}
                    </td>
                  </template>
                  <template v-else>
                    <td :key="'fs-' + pt" class="text-right font-weight-bold">
                      {{
                        formatCurrency(
                          pivotGrandTotal.periods[pt]
                            ? pivotGrandTotal.periods[pt][
                                pivotDisplayMode === "allocated"
                                  ? "allocated"
                                  : pivotDisplayMode === "disbursed"
                                  ? "disbursed"
                                  : "remaining"
                              ]
                            : 0
                        )
                      }}
                    </td>
                  </template>
                </template>
                <template v-if="activePeriodsNumbers.length > 1">
                  <template v-if="pivotDisplayMode === 'all'">
                    <td
                      class="text-right font-weight-black success--text text--darken-3"
                    >
                      {{ formatCurrency(pivotGrandTotal.total_allocated) }}
                    </td>
                    <td
                      class="text-right font-weight-black warning--text text--darken-3"
                    >
                      {{ formatCurrency(pivotGrandTotal.total_disbursed) }}
                    </td>
                    <td
                      class="text-right font-weight-black"
                      :class="
                        pivotGrandTotal.total_remaining < 0
                          ? 'red--text text--darken-2'
                          : 'blue--text text--darken-3'
                      "
                    >
                      {{ formatCurrency(pivotGrandTotal.total_remaining) }}
                    </td>
                  </template>
                  <template v-else>
                    <td class="text-right font-weight-black">
                      {{
                        formatCurrency(
                          pivotDisplayMode === "allocated"
                            ? pivotGrandTotal.total_allocated
                            : pivotDisplayMode === "disbursed"
                            ? pivotGrandTotal.total_disbursed
                            : pivotGrandTotal.total_remaining
                        )
                      }}
                    </td>
                  </template>
                </template>
                <td></td>
              </tr>
            </tfoot>
          </v-simple-table>
        </div>

        <v-card-actions
          class="px-4 py-2 d-flex align-center justify-space-between"
        >
          <div class="text-caption grey--text">
            แสดง {{ pivotPageStart + 1 }}–{{ pivotPageEnd }} จาก
            {{ pivotData.length }} สถานศึกษา
          </div>
          <div class="d-flex align-center">
            <v-select
              v-model="pivotPerPage"
              :items="[10, 20, 50, 100, -1]"
              label="แสดง"
              dense
              outlined
              hide-details
              style="max-width: 90px;"
              class="mr-3"
            >
              <template v-slot:item="{ item }">{{
                item === -1 ? "ทั้งหมด" : item
              }}</template>
              <template v-slot:selection="{ item }">{{
                item === -1 ? "ทั้งหมด" : item
              }}</template>
            </v-select>
            <v-pagination
              v-if="pivotPerPage !== -1"
              v-model="pivotPage"
              :length="pivotTotalPages"
              :total-visible="5"
              circle
              small
            ></v-pagination>
          </div>
        </v-card-actions>
      </v-card>
    </v-container>

    <!-- ===== Detail Dialog ===== -->
    <v-dialog v-model="dialogs.detail" max-width="950">
      <v-card v-if="selectedReport" class="rounded-lg">
        <v-toolbar color="primary" dark flat>
          <v-toolbar-title>{{
            selectedReport.organization_name
          }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogs.detail = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-toolbar>
        <v-card-text class="pa-6">
          <v-row class="mb-4">
            <v-col cols="6" md="3">
              <div class="text-caption grey--text">รหัสสถานศึกษา</div>
              <div class="font-weight-bold">
                {{ selectedReport.college_code }}
              </div>
            </v-col>
            <v-col cols="6" md="3">
              <div class="text-caption grey--text">งวดที่</div>
              <div class="font-weight-bold">
                {{ selectedReport.report_time }} ({{
                  getPeriodLabel(selectedReport.report_time)
                }})
              </div>
            </v-col>
            <v-col cols="6" md="3">
              <div class="text-caption grey--text">ผู้อำนวยการ</div>
              <div class="font-weight-bold">
                {{ selectedReport.director_name }}
              </div>
            </v-col>
            <v-col cols="6" md="3">
              <div class="text-caption grey--text">สถานะ</div>
              <v-chip
                small
                :color="getStatusColor(selectedReport.status)"
                dark
                label
              >
                {{ getStatusText(selectedReport.status) }}
              </v-chip>
            </v-col>
          </v-row>

          <!-- Workforce info in detail dialog -->
          <v-row class="mb-4">
            <v-col cols="4" md="2">
              <v-card outlined class="rounded-lg pa-2 text-center">
                <div class="text-h6 font-weight-bold primary--text">
                  {{
                    selectedReport.total_positions ||
                      selectedReport.total_employees ||
                      0
                  }}
                </div>
                <div class="text-caption grey--text">กรอบอัตรา</div>
              </v-card>
            </v-col>
            <v-col cols="4" md="2">
              <v-card outlined class="rounded-lg pa-2 text-center">
                <div class="text-h6 font-weight-bold success--text">
                  {{
                    selectedReport.filled_positions ||
                      selectedReport.total_employees ||
                      0
                  }}
                </div>
                <div class="text-caption grey--text">มีคนครอง</div>
              </v-card>
            </v-col>
            <v-col cols="4" md="2">
              <v-card
                outlined
                class="rounded-lg pa-2 text-center"
                :style="
                  (selectedReport.vacant_positions || 0) > 0
                    ? 'border-color: #ef5350 !important;'
                    : ''
                "
              >
                <div
                  class="text-h6 font-weight-bold"
                  :class="
                    (selectedReport.vacant_positions || 0) > 0
                      ? 'error--text'
                      : 'grey--text'
                  "
                >
                  {{ selectedReport.vacant_positions || 0 }}
                </div>
                <div class="text-caption grey--text">อัตราว่าง</div>
              </v-card>
            </v-col>
            <v-col cols="4" md="2">
              <v-card outlined class="rounded-lg pa-2 text-center">
                <div class="text-h6 font-weight-bold success--text">
                  {{ formatCurrency(selectedReport.allocated_budget) }}
                </div>
                <div class="text-caption grey--text">เงินโอนจัดสรร</div>
              </v-card>
            </v-col>
            <v-col cols="4" md="2">
              <v-card outlined class="rounded-lg pa-2 text-center">
                <div class="text-h6 font-weight-bold warning--text">
                  {{ formatCurrency(selectedReport.disbursed_budget) }}
                </div>
                <div class="text-caption grey--text">เบิกจ่ายแล้ว</div>
              </v-card>
            </v-col>
            <v-col cols="4" md="2">
              <v-card outlined class="rounded-lg pa-2 text-center">
                <div
                  class="text-h6 font-weight-bold"
                  :class="
                    selectedReport.remaining_budget < 0
                      ? 'error--text'
                      : 'info--text'
                  "
                >
                  {{ formatCurrency(selectedReport.remaining_budget) }}
                </div>
                <div class="text-caption grey--text">คงเหลือ</div>
              </v-card>
            </v-col>
          </v-row>

          <h3 class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon left small color="primary">mdi-calendar-month</v-icon>
            งบประมาณรายเดือน —
            {{ getPeriodMonthsLabel(selectedReport.report_time) }}
          </h3>
          <v-simple-table dense>
            <thead>
              <tr class="grey lighten-4">
                <th>เดือน</th>
                <th class="text-right">เงินโอนจัดสรร</th>
                <th class="text-right">เบิกจ่าย</th>
                <th class="text-right">คงเหลือ</th>
                <th class="text-center" style="width: 100px;">%เบิกจ่าย</th>
                <th>หมายเหตุ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in detailMonthlyData" :key="m.month">
                <td>{{ getMonthName(m.month) }}</td>
                <td class="text-right">
                  {{ formatCurrency(m.allocated_budget) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(m.disbursed_budget) }}
                </td>
                <td
                  class="text-right"
                  :class="{ 'error--text': m.remaining_budget < 0 }"
                >
                  {{ formatCurrency(m.remaining_budget) }}
                </td>
                <td class="text-center">
                  <v-chip
                    x-small
                    :color="getPercentageColor(m.disburse_pct)"
                    dark
                    >{{ m.disburse_pct }}%</v-chip
                  >
                </td>
                <td class="text-caption">{{ m.notes || "-" }}</td>
              </tr>
            </tbody>
            <tfoot v-if="detailMonthlyData.length > 0">
              <tr class="font-weight-bold" style="background: #fafafa;">
                <td>รวม</td>
                <td class="text-right">
                  {{ formatCurrency(detailMonthlyTotal.allocated) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(detailMonthlyTotal.disbursed) }}
                </td>
                <td
                  class="text-right"
                  :class="{ 'error--text': detailMonthlyTotal.remaining < 0 }"
                >
                  {{ formatCurrency(detailMonthlyTotal.remaining) }}
                </td>
                <td class="text-center">
                  <v-chip
                    x-small
                    :color="getPercentageColor(detailMonthlyTotal.pct)"
                    dark
                    >{{ detailMonthlyTotal.pct }}%</v-chip
                  >
                </td>
                <td></td>
              </tr>
            </tfoot>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ===== Send to SNP Dialog ===== -->
    <v-dialog v-model="dialogs.sendSNP" max-width="600" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="headline d-flex align-center">
          <v-icon left color="warning darken-2" large>mdi-send</v-icon>
          ส่งรายงานไปยัง สนผ. สอศ.
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="info" text dense border="left" class="mb-4">
            ระบบจะสร้างไฟล์ Excel สรุปข้อมูลการเบิกจ่ายงบประมาณพนักงานราชการ
            ทุกงวดที่มีรายงาน พร้อมข้อมูลรายเดือนและข้อมูลอัตรากำลัง สำหรับส่ง
            สนผ. สอศ.
          </v-alert>
          <v-select
            v-model="snpReportYear"
            :items="yearOptions"
            label="ปีงบประมาณ"
            outlined
            dense
            prepend-inner-icon="mdi-calendar"
          ></v-select>
          <v-select
            v-model="snpReportTime"
            :items="timeOptionsWithAll"
            item-text="text"
            item-value="value"
            label="งวดการเบิกจ่าย"
            outlined
            dense
            prepend-inner-icon="mdi-clock-time-four-outline"
          ></v-select>
          <v-checkbox
            v-model="snpIncludeMonthly"
            label="รวมข้อมูลรายเดือน"
            hide-details
            class="mb-3"
          ></v-checkbox>
          <v-checkbox
            v-model="snpIncludeWorkforce"
            label="รวมข้อมูลอัตรากำลัง/อัตราว่าง"
            hide-details
            class="mb-3"
          ></v-checkbox>
          <v-checkbox
            v-model="snpOnlyApproved"
            label="เฉพาะรายการที่อนุมัติแล้ว"
            hide-details
          ></v-checkbox>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="dialogs.sendSNP = false">ยกเลิก</v-btn>
          <v-btn
            color="warning darken-2"
            dark
            :loading="isExportingSNP"
            @click="generateSNPReport"
          >
            <v-icon left>mdi-file-excel</v-icon>
            สร้างรายงาน สนผ.
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== PDF Viewer Dialog ===== -->
    <v-dialog v-model="viewFilePdfDialog" max-width="80%">
      <v-card elevation="2" class="rounded-lg">
        <v-toolbar dense flat>
          <v-toolbar-title class="text-subtitle-1">
            <v-icon left>mdi-file-pdf-box</v-icon> ดูไฟล์แนบ
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            :href="
              'https://hrvec.ovec.go.th/HRvec_api/uploads/report/' + currentFile
            "
            target="_blank"
            title="เปิดในแท็บใหม่"
          >
            <v-icon>mdi-open-in-new</v-icon>
          </v-btn>
          <v-btn icon @click="viewFilePdfDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-toolbar>
        <embed
          :src="
            'https://hrvec.ovec.go.th/HRvec_api/uploads/report/' + currentFile
          "
          width="100%"
          height="700px"
          type="application/pdf"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Manpowermenubar_govem from "../../../components/admin/manpowermenubar_govem.vue";
import * as XLSX from "xlsx";

export default {
  components: { Manpowermenubar_govem },

  data: () => ({
    ApiKey: "HRvec2021",
    isLoading: false,
    isExporting: false,
    isExportingFiltered: false,
    isExportingSNP: false,
    searchText: "",
    selectedPeriodTab: 0,
    pivotDisplayMode: "all",
    pivotPage: 1,
    pivotPerPage: 20,

    periodConfig: {
      1: {
        label: "ต.ค.–ธ.ค.",
        months: [10, 11, 12],
        months_label: "ตุลาคม – ธันวาคม"
      },
      2: {
        label: "ม.ค.–ก.พ.",
        months: [1, 2],
        months_label: "มกราคม – กุมภาพันธ์"
      },
      3: { label: "มี.ค.", months: [3], months_label: "มีนาคม" },
      4: {
        label: "เม.ย.–มิ.ย.",
        months: [4, 5, 6],
        months_label: "เมษายน – มิถุนายน"
      },
      5: {
        label: "ก.ค.–ก.ย.",
        months: [7, 8, 9],
        months_label: "กรกฎาคม – กันยายน"
      }
    },

    periodSchedule: [],

    filters: {
      report_year: new Date().getFullYear() + 543,
      report_time: 0,
      status: null,
      selectedUserStatuses: []
    },

    yearOptions: [2567, 2568, 2569, 2570],
    statusOptions: [
      { text: "อนุมัติแล้ว", value: "approved" },
      { text: "รออนุมัติ", value: "submitted" },
      { text: "กำลังตรวจสอบ", value: "checking" },
      { text: "ปฏิเสธ", value: "rejected" },
      { text: "ร่าง", value: "draft" },
      { text: "ยกเลิก", value: "cancelled" }
    ],
    userStatusOptions: [
      { text: "สถานศึกษา", value: "B" },
      { text: "สถาบันการอาชีวศึกษา", value: "C" },
      { text: "สอจ.", value: "G" },
      { text: "ศน.", value: "S" },
      { text: "ส่วนกลาง", value: "V" }
    ],

    rawReports: [],
    rawMonthlyData: [],
    rawWorkforceData: [],
    users: [],
    regions: [],
    provinces: [],

    dialogs: { detail: false, sendSNP: false },
    selectedReport: null,
    detailMonthlyData: [],
    viewFilePdfDialog: false,
    currentFile: "",

    snpReportYear: new Date().getFullYear() + 543,
    snpReportTime: 0,
    snpIncludeMonthly: true,
    snpIncludeWorkforce: true,
    snpOnlyApproved: true,

    periodCollegeHeaders: [
      { text: "กลุ่ม", value: "user_status", width: "80px" },
      { text: "รหัส", value: "college_code", width: "90px" },
      { text: "สถานศึกษา", value: "organization_name" },
      {
        text: "กรอบอัตรา",
        value: "count_manpower",
        width: "80px",
        align: "center"
      },
      {
        text: "มีคนครอง",
        value: "filled_positions",
        width: "80px",
        align: "center"
      },
      {
        text: "อัตราว่าง",
        value: "vacant_positions",
        width: "80px",
        align: "center"
      },
      { text: "สถานะ", value: "status", width: "110px" },
      {
        text: "เงินโอนจัดสรร",
        value: "allocated_budget",
        align: "right",
        width: "130px"
      },
      {
        text: "เบิกจ่าย",
        value: "disbursed_budget",
        align: "right",
        width: "130px"
      },
      {
        text: "คงเหลือ",
        value: "remaining_budget",
        align: "right",
        width: "130px"
      },
      { text: "% เบิกจ่าย", value: "disburse_pct", width: "100px" },
      { text: "หนังสือ", value: "letterFile", width: "60px", align: "center" },
      { text: "ไฟล์", value: "report_file", width: "60px", align: "center" },
      { text: "", value: "actions", sortable: false, width: "50px" }
    ]
  }),

  computed: {
    timeOptionsWithAll() {
      const opts = [{ text: "ทุกงวด", value: 0 }];
      this.enabledPeriodNumbers.forEach(pt => {
        const config = this.periodConfig[pt] || {};
        opts.push({ text: `งวดที่ ${pt} (${config.label || ""})`, value: pt });
      });
      return opts;
    },

    enabledPeriodNumbers() {
      if (this.periodSchedule.length === 0) return [1, 2, 3, 4, 5];
      return this.periodSchedule
        .filter(
          s =>
            s.report_year == this.filters.report_year &&
            (s.period_status === "open" || s.period_status === "closed")
        )
        .map(s => parseInt(s.report_time))
        .sort((a, b) => a - b);
    },

    filteredData() {
      return this.rawReports.filter(r => {
        const timeMatch =
          this.filters.report_time === 0 ||
          r.report_time == this.filters.report_time;
        const statusMatch =
          !this.filters.status || r.status === this.filters.status;
        const userStatusMatch =
          this.filters.selectedUserStatuses.length === 0 ||
          this.filters.selectedUserStatuses.includes(r.user_status_raw);
        return timeMatch && statusMatch && userStatusMatch;
      });
    },

    overallSummary() {
      const data = this.filteredData;
      const totalAllocated = data.reduce(
        (s, r) => s + parseFloat(r.allocated_budget || 0),
        0
      );
      const totalDisbursed = data.reduce(
        (s, r) => s + parseFloat(r.disbursed_budget || 0),
        0
      );
      const totalRemaining = data.reduce(
        (s, r) => s + parseFloat(r.remaining_budget || 0),
        0
      );
      const pct =
        totalAllocated > 0
          ? Math.round((totalDisbursed / totalAllocated) * 100)
          : 0;
      return {
        totalReports: data.length,
        totalColleges: new Set(data.map(r => r.college_code)).size,
        totalAllocated,
        totalDisbursed,
        totalRemaining,
        disbursePercentage: pct
      };
    },

    // Workforce summary computed from reports data
    workforceSummary() {
      const data = this.filteredData;
      // Use the latest report per college for workforce counts
      const latestByCollege = {};
      data.forEach(r => {
        const code = r.college_code;
        if (
          !latestByCollege[code] ||
          parseInt(r.report_time) > parseInt(latestByCollege[code].report_time)
        ) {
          latestByCollege[code] = r;
        }
      });

      const colleges = Object.values(latestByCollege);
      let totalPositions = 0;
      let filledPositions = 0;
      let vacantPositions = 0;
      let maleCount = 0;
      let femaleCount = 0;
      const vacantColleges = [];

      colleges.forEach(r => {
        const total =
          parseInt(r.total_positions) || parseInt(r.total_employees) || 0;
        const filled =
          parseInt(r.filled_positions) || parseInt(r.total_employees) || 0;
        const vacant = parseInt(r.vacant_positions) || 0;

        totalPositions += total;
        filledPositions += filled;
        vacantPositions += vacant;
        maleCount += parseInt(r.male_count) || 0;
        femaleCount += parseInt(r.female_count) || 0;

        if (vacant > 0) {
          vacantColleges.push({
            college_code: r.college_code,
            college_name: r.organization_name,
            vacant: vacant
          });
        }
      });

      const filledPct =
        totalPositions > 0
          ? Math.round((filledPositions / totalPositions) * 100)
          : 0;

      return {
        totalPositions,
        filledPositions,
        vacantPositions,
        filledPercentage: filledPct,
        maleCount,
        femaleCount,
        vacantCollegeCount: vacantColleges.length,
        topVacantColleges: vacantColleges.sort((a, b) => b.vacant - a.vacant)
      };
    },

    activePeriods() {
      const map = {};
      this.filteredData.forEach(r => {
        const key = r.report_time;
        if (!map[key]) {
          const config = this.periodConfig[key] || {
            label: "",
            months: [],
            months_label: ""
          };
          map[key] = {
            report_time: key,
            period_label: config.label,
            months_label: config.months_label,
            months: config.months,
            count: 0,
            college_count: 0,
            total_allocated: 0,
            total_disbursed: 0,
            total_remaining: 0,
            total_positions: 0,
            filled_positions: 0,
            vacant_positions: 0,
            colleges: new Set()
          };
        }
        map[key].count++;
        map[key].colleges.add(r.college_code);
        map[key].total_allocated += parseFloat(r.allocated_budget || 0);
        map[key].total_disbursed += parseFloat(r.disbursed_budget || 0);
       map[key].total_remaining = map[key].total_allocated - map[key].total_disbursed;
        map[key].total_positions +=
          parseInt(r.total_positions) || parseInt(r.total_employees) || 0;
        map[key].filled_positions +=
          parseInt(r.filled_positions) || parseInt(r.total_employees) || 0;
        map[key].vacant_positions += parseInt(r.vacant_positions) || 0;
      });
      return Object.values(map)
        .map(p => ({
          ...p,
          college_count: p.colleges.size,
          disburse_pct:
            p.total_allocated > 0
              ? Math.round((p.total_disbursed / p.total_allocated) * 100)
              : 0
        }))
        .sort((a, b) => a.report_time - b.report_time);
    },

    allPeriodsDisplay() {
      return this.enabledPeriodNumbers.map(t => {
        const active = this.activePeriods.find(
          p => parseInt(p.report_time) === parseInt(t)
        );
        const config = this.periodConfig[t] || {
          label: "",
          months: [],
          months_label: ""
        };
        const schedule = this.periodSchedule.find(
          s =>
            s.report_year == this.filters.report_year &&
            parseInt(s.report_time) === t
        );
        const isOpen = schedule ? schedule.period_status === "open" : false;
        const openDate = schedule ? schedule.open_date : null;
        const closeDate = schedule ? schedule.close_date : null;

        if (active)
          return { ...active, hasData: true, isOpen, openDate, closeDate };
        return {
          report_time: t,
          period_label: config.label,
          months_label: config.months_label,
          months: config.months,
          hasData: false,
          isOpen,
          openDate,
          closeDate,
          count: 0,
          college_count: 0,
          total_allocated: 0,
          total_disbursed: 0,
          total_remaining: 0,
          total_positions: 0,
          filled_positions: 0,
          vacant_positions: 0,
          disburse_pct: 0
        };
      });
    },

    lastActivePeriod() {
      if (this.activePeriods.length === 0) return 0;
      return this.activePeriods[this.activePeriods.length - 1].report_time;
    },

    detailMonthlyTotal() {
      const data = this.detailMonthlyData;
      const allocated = data.reduce(
        (s, m) => s + parseFloat(m.allocated_budget || 0),
        0
      );
      const disbursed = data.reduce(
        (s, m) => s + parseFloat(m.disbursed_budget || 0),
        0
      );
      const remaining = data.reduce(
        (s, m) => s + parseFloat(m.remaining_budget || 0),
        0
      );
      return {
        allocated,
        disbursed,
        remaining,
        pct: allocated > 0 ? Math.round((disbursed / allocated) * 100) : 0
      };
    },

    activePeriodsNumbers() {
      const nums = new Set(this.filteredData.map(r => parseInt(r.report_time)));
      return [...nums].sort((a, b) => a - b);
    },

    pivotData() {
      const map = {};
      this.filteredData.forEach(r => {
        const code = r.college_code;
        if (!map[code]) {
          map[code] = {
            college_code: code,
            organization_code: r.organization_code || "",
            allocation_code: r.allocation_code || "",
            organization_name: r.organization_name || "",
            province_name: r.province_name || "",
            user_status: r.user_status || "",
            user_status_raw: r.user_status_raw || "",
            total_employees: r.total_employees || 0,
            total_positions:
              parseInt(r.total_positions) || parseInt(r.total_employees) || 0,
            filled_positions:
              parseInt(r.filled_positions) || parseInt(r.total_employees) || 0,
            vacant_positions: parseInt(r.vacant_positions) || 0,
            periods: {},
            total_allocated: 0,
            total_disbursed: 0,
            total_remaining: 0,
            reports: []
          };
        }
        const pt = parseInt(r.report_time);
        const allocated = parseFloat(r.allocated_budget) || 0;
        const disbursed = parseFloat(r.disbursed_budget) || 0;
        const remaining = parseFloat(r.remaining_budget) || 0;

        if (!map[code].periods[pt])
          map[code].periods[pt] = { allocated: 0, disbursed: 0, remaining: 0 };
        map[code].periods[pt].allocated += allocated;
        map[code].periods[pt].disbursed += disbursed;
        map[code].periods[pt].remaining += remaining;
        map[code].total_allocated += allocated;
        map[code].total_disbursed += disbursed;
       map[code].total_remaining = map[code].total_allocated - map[code].total_disbursed;

        // Update workforce to latest period
        if (!map[code]._latestPeriod || pt > map[code]._latestPeriod) {
          map[code]._latestPeriod = pt;
          map[code].total_positions =
            parseInt(r.total_positions) || parseInt(r.total_employees) || 0;
          map[code].filled_positions =
            parseInt(r.filled_positions) || parseInt(r.total_employees) || 0;
          map[code].vacant_positions = parseInt(r.vacant_positions) || 0;
        }
        map[code].reports.push(r);
      });

      let result = Object.values(map);
      if (this.searchText) {
        const search = this.searchText.toLowerCase();
        result = result.filter(
          r =>
            r.college_code.toLowerCase().includes(search) ||
            r.organization_name.toLowerCase().includes(search) ||
            r.organization_code.toLowerCase().includes(search)
        );
      }
      return result.sort((a, b) =>
        a.college_code.localeCompare(b.college_code)
      );
    },

    filteredPivotData() {
      if (this.pivotPerPage === -1) return this.pivotData;
      return this.pivotData.slice(this.pivotPageStart, this.pivotPageEnd);
    },

    pivotPageStart() {
      if (this.pivotPerPage === -1) return 0;
      return (this.pivotPage - 1) * this.pivotPerPage;
    },

    pivotPageEnd() {
      if (this.pivotPerPage === -1) return this.pivotData.length;
      return Math.min(
        this.pivotPage * this.pivotPerPage,
        this.pivotData.length
      );
    },

    pivotTotalPages() {
      if (this.pivotPerPage === -1) return 1;
      return Math.ceil(this.pivotData.length / this.pivotPerPage);
    },

    pivotGrandTotal() {
      const result = {
        periods: {},
        total_allocated: 0,
        total_disbursed: 0,
        total_remaining: 0,
        total_positions: 0,
        filled_positions: 0,
        vacant_positions: 0
      };
      this.pivotData.forEach(row => {
        result.total_allocated += row.total_allocated;
        result.total_disbursed += row.total_disbursed;
        result.total_remaining += row.total_remaining;
        result.total_positions += row.total_positions;
        result.filled_positions += row.filled_positions;
        result.vacant_positions += row.vacant_positions;
        this.activePeriodsNumbers.forEach(pt => {
          if (!result.periods[pt])
            result.periods[pt] = { allocated: 0, disbursed: 0, remaining: 0 };
          if (row.periods[pt]) {
            result.periods[pt].allocated += row.periods[pt].allocated;
            result.periods[pt].disbursed += row.periods[pt].disbursed;
            result.periods[pt].remaining += row.periods[pt].remaining;
          }
        });
      });
      return result;
    }
  },

  async mounted() {
    await this.fetchMasterData();
    await this.fetchData();
  },

  methods: {
    async fetchMasterData() {
      this.isLoading = true;
      try {
        const [provRes, regionRes, userRes, scheduleRes] = await Promise.all([
          this.$http.post("province.php", { ApiKey: this.ApiKey }),
          this.$http.post("region.php", { ApiKey: this.ApiKey }),
          this.$http.post("user.php", {
            ApiKey: this.ApiKey,
            show_user_gov: "ok"
          }),
          this.$http.post("government_employee_report_complete.php", {
            ApiKey: this.ApiKey,
            action: "get_period_schedule",
            report_year: this.filters.report_year
          })
        ]);
        this.provinces = provRes.data;
        this.regions = regionRes.data;
        this.users = Array.isArray(userRes.data) ? userRes.data : [];
        if (scheduleRes.data.status)
          this.periodSchedule = scheduleRes.data.data || [];
      } catch (e) {
        console.error("Error fetching master data", e);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchData() {
      this.isLoading = true;
      try {
        const scheduleRes = await this.$http.post(
          "government_employee_report_complete.php",
          {
            ApiKey: this.ApiKey,
            action: "get_period_schedule",
            report_year: this.filters.report_year
          }
        );
        if (scheduleRes.data.status)
          this.periodSchedule = scheduleRes.data.data || [];

        const payload = {
          ApiKey: this.ApiKey,
          report_year: this.filters.report_year,
          action: "get_reports"
        };
        if (this.filters.report_time > 0)
          payload.report_time = this.filters.report_time;
        if (this.filters.status) payload.status = this.filters.status;

        const [reportsRes, monthlyRes] = await Promise.all([
          this.$http.post("government_employee_report_complete.php", payload),
          this.$http.post("government_employee_report_complete.php", {
            ApiKey: this.ApiKey,
            report_year: this.filters.report_year,
            ...(this.filters.report_time > 0
              ? { report_time: this.filters.report_time }
              : {}),
            action: "get_monthly_budget_details"
          })
        ]);

        if (reportsRes.data.status) {
          this.rawReports = (reportsRes.data.data || []).map(r => {
            const user = this.users.find(u => u.user_code === r.college_code);
            const allocated = parseFloat(r.allocated_budget) || 0;
            const disbursed = parseFloat(r.disbursed_budget) || 0;
            return {
              ...r,
              user_status: this.groupType(user ? user.user_status : ""),
              user_status_raw: user ? user.user_status : "",
              province_name: r.province_name || "",
              region_name: r.region_name || "",
              allocated_budget: allocated,
              disbursed_budget: disbursed,
              remaining_budget: parseFloat(r.remaining_budget) || 0,
              disburse_pct:
                allocated > 0 ? Math.round((disbursed / allocated) * 100) : 0,
              // Workforce fields
              total_positions:
                parseInt(r.total_positions) || parseInt(r.total_employees) || 0,
              filled_positions:
                parseInt(r.filled_positions) ||
                parseInt(r.total_employees) ||
                0,
              vacant_positions: parseInt(r.vacant_positions) || 0
            };
          });
        }

        if (monthlyRes.data.status) {
          this.rawMonthlyData = (monthlyRes.data.data || []).map(m => ({
            ...m,
            allocated_budget: parseFloat(m.allocated_budget) || 0,
            disbursed_budget: parseFloat(m.disbursed_budget) || 0,
            remaining_budget: parseFloat(m.remaining_budget) || 0,
            disburse_percentage: parseFloat(m.disburse_percentage) || 0,
            report_time: (() => {
              const report = this.rawReports.find(rr => rr.id == m.report_id);
              return report ? report.report_time : "";
            })()
          }));
        }
      } catch (e) {
        console.error("Fetch error", e);
      } finally {
        this.isLoading = false;
      }
    },

    getPeriodReports(reportTime) {
      return this.filteredData.filter(r => r.report_time == reportTime);
    },

    getPeriodMonthlyBreakdown(reportTime) {
      const periodReports = this.filteredData.filter(
        r => r.report_time == reportTime
      );
      const reportIds = new Set(periodReports.map(r => r.id));
      const monthlyForPeriod = this.rawMonthlyData.filter(m =>
        reportIds.has(m.report_id)
      );
      const monthMap = {};
      monthlyForPeriod.forEach(m => {
        const month = parseInt(m.month);
        if (!monthMap[month])
          monthMap[month] = {
            month,
            total_allocated: 0,
            total_disbursed: 0,
            total_remaining: 0
          };
        monthMap[month].total_allocated += parseFloat(m.allocated_budget) || 0;
        monthMap[month].total_disbursed += parseFloat(m.disbursed_budget) || 0;
        monthMap[month].total_remaining += parseFloat(m.remaining_budget) || 0;
      });
      const fiscalOrder = [10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8, 9];
      return Object.values(monthMap)
        .map(m => ({
          ...m,
          disburse_pct:
            m.total_allocated > 0
              ? Math.round((m.total_disbursed / m.total_allocated) * 100)
              : 0
        }))
        .sort(
          (a, b) => fiscalOrder.indexOf(a.month) - fiscalOrder.indexOf(b.month)
        );
    },

    getPeriodLabel(reportTime) {
      const config = this.periodConfig[reportTime];
      return config ? config.label : "";
    },

    getPeriodMonthsLabel(reportTime) {
      const config = this.periodConfig[reportTime];
      return config ? config.months_label : "";
    },

    viewDetail(item) {
      this.selectedReport = item;
      if (item.monthly_budget_json) {
        try {
          const json =
            typeof item.monthly_budget_json === "string"
              ? JSON.parse(item.monthly_budget_json)
              : item.monthly_budget_json;
          this.detailMonthlyData = Object.keys(json).map(k => {
            const allocated = parseFloat(json[k].allocated) || 0;
            const disbursed = parseFloat(json[k].disbursed) || 0;
            return {
              month: parseInt(k),
              allocated_budget: allocated,
              disbursed_budget: disbursed,
              remaining_budget: parseFloat(json[k].remaining) || 0,
              disburse_pct:
                allocated > 0 ? Math.round((disbursed / allocated) * 100) : 0,
              notes: json[k].notes || ""
            };
          });
        } catch {
          this.detailMonthlyData = [];
        }
      } else {
        this.detailMonthlyData = this.rawMonthlyData
          .filter(m => m.report_id == item.id)
          .map(m => {
            const allocated = parseFloat(m.allocated_budget) || 0;
            const disbursed = parseFloat(m.disbursed_budget) || 0;
            return {
              ...m,
              disburse_pct:
                allocated > 0 ? Math.round((disbursed / allocated) * 100) : 0
            };
          });
      }
      this.dialogs.detail = true;
    },

    viewCollegeAllPeriods(row) {
      if (row.reports && row.reports.length > 0)
        this.viewDetail(row.reports[0]);
    },

    openFileViewer(fileName) {
      this.currentFile = fileName;
      this.viewFilePdfDialog = true;
    },

    resetFilters() {
      this.filters.report_time = 0;
      this.filters.status = null;
      this.filters.selectedUserStatuses = [];
      this.searchText = "";
      this.selectedPeriodTab = 0;
      this.fetchData();
    },

    openSendToSNPDialog() {
      this.snpReportYear = this.filters.report_year;
      this.snpReportTime = this.filters.report_time;
      this.dialogs.sendSNP = true;
    },

    // ===== EXPORTS =====
    exportPeriodData(reportTime) {
      const periodReports = this.getPeriodReports(reportTime);
      const config = this.periodConfig[reportTime] || {};
      const data = periodReports.map((r, idx) => ({
        ลำดับ: idx + 1,
        กลุ่ม: r.user_status,
        รหัสหน่วยงาน: r.organization_code,
        รหัสหน่วยเบิก: r.allocation_code,
        รหัสสถานศึกษา: r.college_code,
        ชื่อสถานศึกษา: r.organization_name,
        จังหวัด: r.province_name,
        กรอบอัตรากำลัง: r.total_positions || r.total_employees || 0,
        มีคนครองตำแหน่ง: r.filled_positions || r.total_employees || 0,
        อัตราว่าง: r.vacant_positions || 0,
        สถานะ: this.getStatusText(r.status),
        "เงินโอนจัดสรร (บาท)": r.allocated_budget,
        "เบิกจ่าย (บาท)": r.disbursed_budget,
        "คงเหลือ (บาท)": r.remaining_budget,
        "อัตราเบิกจ่าย (%)": r.disburse_pct
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `งวดที่${reportTime}`);
      XLSX.writeFile(
        wb,
        `รายงานเบิกจ่าย_งวด${reportTime}_${config.label || ""}_${
          this.filters.report_year
        }.xlsx`
      );
    },

    exportPivotData() {
      this.isExportingFiltered = true;
      try {
        const wb = XLSX.utils.book_new();
        const periods = this.activePeriodsNumbers;
        const rows = this.pivotData.map((row, idx) => {
          const obj = {
            ลำดับ: idx + 1,
            กลุ่ม: row.user_status,
            รหัสสถานศึกษา: row.college_code,
            รหัสหน่วยงาน: row.organization_code,
            รหัสหน่วยเบิก: row.allocation_code,
            สถานศึกษา: row.organization_name,
            จังหวัด: row.province_name,
            กรอบอัตรากำลัง: row.total_positions,
            มีคนครองตำแหน่ง: row.filled_positions,
            อัตราว่าง: row.vacant_positions
          };
          periods.forEach(pt => {
            const config = this.periodConfig[pt] || {};
            const prefix = `งวด${pt}(${config.label || ""})`;
            if (row.periods[pt]) {
              obj[`${prefix}_จัดสรร`] = row.periods[pt].allocated;
              obj[`${prefix}_เบิกจ่าย`] = row.periods[pt].disbursed;
              obj[`${prefix}_คงเหลือ`] = row.periods[pt].remaining;
            } else {
              obj[`${prefix}_จัดสรร`] = 0;
              obj[`${prefix}_เบิกจ่าย`] = 0;
              obj[`${prefix}_คงเหลือ`] = 0;
            }
          });
          obj["รวม_จัดสรร"] = row.total_allocated;
          obj["รวม_เบิกจ่าย"] = row.total_disbursed;
          obj["รวม_คงเหลือ"] = row.total_remaining;
          obj["อัตราเบิกจ่าย(%)"] =
            row.total_allocated > 0
              ? Math.round((row.total_disbursed / row.total_allocated) * 100)
              : 0;
          return obj;
        });
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(rows),
          "สรุปรายสถานศึกษา"
        );
        XLSX.writeFile(
          wb,
          `สรุปเบิกจ่ายรายสถานศึกษา_ทุกงวด_${this.filters.report_year}.xlsx`
        );
      } finally {
        this.isExportingFiltered = false;
      }
    },

    exportFullReport() {
      this.isExporting = true;
      try {
        const wb = XLSX.utils.book_new();

        // Sheet 1: สรุปตามงวด
        const periodData = this.activePeriods.map(p => ({
          งวดที่: p.report_time,
          ช่วงเวลา: p.period_label,
          เดือน: p.months_label,
          จำนวนรายงาน: p.count,
          จำนวนสถานศึกษา: p.college_count,
          กรอบอัตรากำลัง: p.total_positions,
          มีคนครอง: p.filled_positions,
          อัตราว่าง: p.vacant_positions,
          "เงินโอนจัดสรร (บาท)": p.total_allocated,
          "เบิกจ่าย (บาท)": p.total_disbursed,
          "คงเหลือ (บาท)": p.total_remaining,
          "อัตราเบิกจ่าย (%)": p.disburse_pct
        }));
        periodData.push({
          งวดที่: "รวม",
          ช่วงเวลา: "",
          เดือน: "",
          จำนวนรายงาน: this.overallSummary.totalReports,
          จำนวนสถานศึกษา: this.overallSummary.totalColleges,
          กรอบอัตรากำลัง: this.workforceSummary.totalPositions,
          มีคนครอง: this.workforceSummary.filledPositions,
          อัตราว่าง: this.workforceSummary.vacantPositions,
          "เงินโอนจัดสรร (บาท)": this.overallSummary.totalAllocated,
          "เบิกจ่าย (บาท)": this.overallSummary.totalDisbursed,
          "คงเหลือ (บาท)": this.overallSummary.totalRemaining,
          "อัตราเบิกจ่าย (%)": this.overallSummary.disbursePercentage
        });
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(periodData),
          "สรุปตามงวด"
        );

        // Sheet 2: รายสถานศึกษา
        const summaryData = this.rawReports.map((r, idx) => ({
          ลำดับ: idx + 1,
          กลุ่ม: r.user_status,
          รหัสสถานศึกษา: r.college_code,
          รหัสหน่วยงาน: r.organization_code,
          รหัสหน่วยเบิก: r.allocation_code,
          ชื่อสถานศึกษา: r.organization_name,
          งวดที่: r.report_time,
          สถานะ: this.getStatusText(r.status),
          กรอบอัตรากำลัง: r.total_positions,
          มีคนครอง: r.filled_positions,
          อัตราว่าง: r.vacant_positions,
          "เงินโอนจัดสรร (บาท)": r.allocated_budget,
          "เบิกจ่าย (บาท)": r.disbursed_budget,
          "คงเหลือ (บาท)": r.remaining_budget,
          "อัตราเบิกจ่าย (%)": r.disburse_pct
        }));
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(summaryData),
          "รายสถานศึกษา"
        );

        // Sheet 3: รายเดือน
        const monthlyData = this.rawMonthlyData.map(m => {
          const report = this.rawReports.find(r => r.id == m.report_id);
          return {
            รหัสสถานศึกษา: report ? report.college_code : m.college_code || "",
            ชื่อสถานศึกษา: report
              ? report.organization_name
              : m.organization_name || "",
            งวดที่: report ? report.report_time : "",
            เดือน: this.getMonthName(m.month),
            "จัดสรร (บาท)": m.allocated_budget,
            "เบิกจ่าย (บาท)": m.disbursed_budget,
            "คงเหลือ (บาท)": m.remaining_budget,
            "อัตราเบิกจ่าย (%)": m.disburse_percentage,
            หมายเหตุ: m.notes || ""
          };
        });
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(monthlyData),
          "รายเดือน"
        );

        XLSX.writeFile(
          wb,
          `รายงานเบิกจ่ายฉบับเต็ม_${this.filters.report_year}.xlsx`
        );
      } finally {
        this.isExporting = false;
      }
    },

    async generateSNPReport() {
      this.isExportingSNP = true;
      try {
        const payload = {
          ApiKey: this.ApiKey,
          report_year: this.snpReportYear,
          action: "get_export_data_full"
        };
        if (this.snpReportTime > 0) payload.report_time = this.snpReportTime;

        const res = await this.$http.post(
          "government_employee_report_complete.php",
          payload
        );
        if (!res.data.status) return;

        let exportData = res.data.data || [];
        if (this.snpOnlyApproved)
          exportData = exportData.filter(d => d.status === "approved");

        const wb = XLSX.utils.book_new();

        // Sheet 1: สรุปสถานศึกษา (with workforce)
        const collegeMap = {};
        exportData.forEach(d => {
          if (!collegeMap[d.college_code]) {
            collegeMap[d.college_code] = {
              รหัส: d.college_code,
              รหัสหน่วยงาน: d.organization_code || "",
              รหัสหน่วยเบิก: d.allocation_code || "",
              สถานศึกษา: d.organization_name,
              ปีงบประมาณ: d.report_year,
              งวดที่: d.report_time,
              กรอบอัตรากำลัง:
                parseInt(d.total_positions) || parseInt(d.total_employees) || 0,
              มีคนครองตำแหน่ง:
                parseInt(d.filled_positions) ||
                parseInt(d.total_employees) ||
                0,
              อัตราว่าง: parseInt(d.vacant_positions) || 0,
              ผู้อำนวยการ: d.director_name || "",
              สถานะ: this.getStatusText(d.status),
              "งบโอนจัดสรรรวม (บาท)": 0,
              "เบิกจ่ายรวม (บาท)": 0,
              "คงเหลือรวม (บาท)": 0
            };
          }
          collegeMap[d.college_code]["งบโอนจัดสรรรวม (บาท)"] +=
            parseFloat(d.allocated_budget) || 0;
          collegeMap[d.college_code]["เบิกจ่ายรวม (บาท)"] +=
            parseFloat(d.disbursed_budget) || 0;
          collegeMap[d.college_code]["คงเหลือรวม (บาท)"] =
            collegeMap[d.college_code]["งบโอนจัดสรรรวม (บาท)"] -
            collegeMap[d.college_code]["เบิกจ่ายรวม (บาท)"];
        });
        const collegeSummary = Object.values(collegeMap);
        collegeSummary.forEach(c => {
          c["อัตราเบิกจ่าย (%)"] =
            c["งบโอนจัดสรรรวม (บาท)"] > 0
              ? Math.round(
                  (c["เบิกจ่ายรวม (บาท)"] / c["งบโอนจัดสรรรวม (บาท)"]) * 100
                )
              : 0;
        });
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(collegeSummary),
          "สรุปสถานศึกษา"
        );

        if (this.snpIncludeMonthly) {
          const monthlyExport = exportData
            .filter(d => d.month != null)
            .map(d => ({
              รหัส: d.college_code,
              รหัสหน่วยงาน: d.organization_code || "",
              รหัสหน่วยเบิก: d.allocation_code || "",
              สถานศึกษา: d.organization_name,
              งวดที่: d.report_time,
              เดือน: this.getMonthName(d.month),
              "จัดสรร (บาท)": parseFloat(d.allocated_budget) || 0,
              "เบิกจ่าย (บาท)": parseFloat(d.disbursed_budget) || 0,
              "คงเหลือ (บาท)": parseFloat(d.remaining_budget) || 0,
              "อัตราเบิกจ่าย (%)": parseFloat(d.disburse_percentage) || 0,
              หมายเหตุ: d.notes || ""
            }));
          XLSX.utils.book_append_sheet(
            wb,
            XLSX.utils.json_to_sheet(monthlyExport),
            "รายเดือน"
          );
        }

        // Sheet: ภาพรวม
        const totalAllocated = collegeSummary.reduce(
          (s, c) => s + c["งบโอนจัดสรรรวม (บาท)"],
          0
        );
        const totalDisbursed = collegeSummary.reduce(
          (s, c) => s + c["เบิกจ่ายรวม (บาท)"],
          0
        );
        const totalPositions = collegeSummary.reduce(
          (s, c) => s + c["กรอบอัตรากำลัง"],
          0
        );
        const filledPositions = collegeSummary.reduce(
          (s, c) => s + c["มีคนครองตำแหน่ง"],
          0
        );
        const vacantPositions = collegeSummary.reduce(
          (s, c) => s + c["อัตราว่าง"],
          0
        );

        const overviewData = [
          { รายการ: "ปีงบประมาณ", ค่า: this.snpReportYear },
          {
            รายการ: "งวดที่",
            ค่า: this.snpReportTime === 0 ? "ทุกงวด" : this.snpReportTime
          },
          { รายการ: "จำนวนสถานศึกษาที่รายงาน", ค่า: collegeSummary.length },
          { รายการ: "กรอบอัตรากำลังรวม", ค่า: totalPositions },
          { รายการ: "มีคนครองตำแหน่ง", ค่า: filledPositions },
          { รายการ: "อัตราว่างรวม", ค่า: vacantPositions },
          {
            รายการ: "อัตราครองตำแหน่ง (%)",
            ค่า:
              totalPositions > 0
                ? Math.round((filledPositions / totalPositions) * 100)
                : 0
          },
          { รายการ: "เงินโอนจัดสรรรวม (บาท)", ค่า: totalAllocated },
          { รายการ: "เบิกจ่ายรวม (บาท)", ค่า: totalDisbursed },
          { รายการ: "คงเหลือรวม (บาท)", ค่า: totalAllocated - totalDisbursed },
          {
            รายการ: "อัตราเบิกจ่ายรวม (%)",
            ค่า:
              totalAllocated > 0
                ? Math.round((totalDisbursed / totalAllocated) * 100)
                : 0
          },
          {
            รายการ: "วันที่ออกรายงาน",
            ค่า: new Date().toLocaleDateString("th-TH", {
              year: "numeric",
              month: "long",
              day: "numeric"
            })
          }
        ];
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(overviewData),
          "ภาพรวม"
        );

        const timeLabel =
          this.snpReportTime === 0 ? "ทุกงวด" : `งวด${this.snpReportTime}`;
        XLSX.writeFile(
          wb,
          `รายงานสนผ_งบพนักงานราชการ_${this.snpReportYear}_${timeLabel}.xlsx`
        );
        this.dialogs.sendSNP = false;
      } catch (e) {
        console.error("SNP Report Error:", e);
      } finally {
        this.isExportingSNP = false;
      }
    },

    // ===== HELPERS =====
    formatCurrency(value) {
      return new Intl.NumberFormat("th-TH", {
        style: "currency",
        currency: "THB"
      }).format(value || 0);
    },
    formatCurrencyShort(value) {
      const num = parseFloat(value) || 0;
      if (Math.abs(num) >= 1e9) return (num / 1e9).toFixed(1) + " พันล้าน";
      if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(1) + " ล้าน";
      if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(0) + " พัน";
      return num.toFixed(0);
    },
    getStatusColor(status) {
      return (
        {
          approved: "success",
          submitted: "info",
          checking: "warning",
          rejected: "error",
          cancelled: "orange",
          draft: "grey"
        }[status] || "grey"
      );
    },
    getStatusText(status) {
      return (
        {
          approved: "อนุมัติแล้ว",
          submitted: "รออนุมัติ",
          checking: "กำลังตรวจสอบ",
          rejected: "ปฏิเสธ",
          cancelled: "ยกเลิก",
          draft: "ร่าง"
        }[status] || status
      );
    },
    getStatusIcon(status) {
      return (
        {
          approved: "mdi-check",
          submitted: "mdi-clock",
          checking: "mdi-magnify",
          rejected: "mdi-close",
          cancelled: "mdi-cancel",
          draft: "mdi-pencil"
        }[status] || "mdi-help"
      );
    },
    getPercentageColor(val) {
      if (val > 80) return "success";
      if (val > 50) return "warning";
      return "error";
    },
    getUserStatusColor(code) {
      return (
        { B: "blue", C: "primary", G: "teal", S: "orange", V: "red" }[code] ||
        "grey"
      );
    },
    groupType(status) {
      return (
        { B: "สถานศึกษา", C: "สถาบันฯ", G: "สอจ.", S: "ศน.", V: "ส่วนกลาง" }[
          status
        ] || "-"
      );
    },
    getMonthName(m) {
      return (
        [
          "",
          "มกราคม",
          "กุมภาพันธ์",
          "มีนาคม",
          "เมษายน",
          "พฤษภาคม",
          "มิถุนายน",
          "กรกฎาคม",
          "สิงหาคม",
          "กันยายน",
          "ตุลาคม",
          "พฤศจิกายน",
          "ธันวาคม"
        ][m] || m
      );
    },
    formatDateThai(dateStr) {
      if (!dateStr) return "";
      try {
        return new Date(dateStr).toLocaleDateString("th-TH", {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
      } catch {
        return dateStr;
      }
    }
  }
};
</script>

<style scoped>
.report-bg {
  background: linear-gradient(135deg, #f5f5f5 0%, #f5f5f5 100%);
  min-height: 100vh;
}
.header-card {
  overflow: hidden;
}
.header-title-bar {
  background: linear-gradient(135deg, #455a64 0%, #455a64 50%, #8e1014 100%);
}
.filter-section {
  background: linear-gradient(180deg, #f5f5f5 0%, #f5f5f5 100%);
  border-top: 1px solid #f5f5f5;
}

/* Summary Cards */
.summary-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
  position: relative;
}
.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12) !important;
}
.summary-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}
.summary-card--total::before {
  background: linear-gradient(90deg, #b3151b, #b3151b);
}
.summary-card--allocated::before {
  background: linear-gradient(90deg, #2e7d32, #2e7d32);
}
.summary-card--disbursed::before {
  background: linear-gradient(90deg, #A86400, #A86400);
}
.summary-card--remaining::before {
  background: linear-gradient(90deg, #c62828, #ef5350);
}
.summary-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.summary-icon--indigo {
  background: linear-gradient(135deg, #b3151b, #b3151b);
}
.summary-icon--green {
  background: linear-gradient(135deg, #2e7d32, #2e7d32);
}
.summary-icon--orange {
  background: linear-gradient(135deg, #A86400, #A86400);
}
.summary-icon--red {
  background: linear-gradient(135deg, #c62828, #ef5350);
}

/* Workforce Section */
.workforce-header {
  background: linear-gradient(135deg, #37474f 0%, #546e7a 100%);
}
.workforce-stat-card {
  transition: all 0.2s ease;
  border: 1px solid #e0e0e0 !important;
}
.workforce-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.workforce-stat-card--alert {
  border-color: #ffcdd2 !important;
  background: linear-gradient(180deg, #ffffff 0%, #fff5f5 100%);
}

/* Period Summary */
.period-summary-card {
  border: none;
}
.period-summary-header {
  background: linear-gradient(135deg, #b3151b 0%, #b3151b 100%);
}
.period-tabs .v-tab {
  letter-spacing: 0;
}
.period-detail-card {
  transition: all 0.2s ease;
  border: 2px solid transparent !important;
}
.period-detail-card--active {
  border-color: #fbecec !important;
}
.period-detail-card--active:hover {
  border-color: #d9777b !important;
  box-shadow: 0 4px 15px rgba(103, 58, 183, 0.1);
}
.period-detail-card--inactive {
  opacity: 0.6;
  background: #fafafa;
}
.period-detail-header {
  background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
}
.budget-row {
  padding: 4px 0;
}

/* Budget Highlight Cards */
.budget-highlight-card {
  transition: all 0.2s ease;
}
.budget-highlight-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}
.budget-highlight--allocated {
  border-color: #81c784 !important;
  background: linear-gradient(180deg, #ffffff 0%, #e8f5e9 100%);
}
.budget-highlight--disbursed {
  border-color: #e6b04d !important;
  background: linear-gradient(180deg, #ffffff 0%, #fff4dc 100%);
}
.budget-highlight--remaining {
  border-color: #90a4ae !important;
  background: linear-gradient(180deg, #ffffff 0%, #f5f5f5 100%);
}
.budget-highlight--workforce {
  border-color: #d9777b !important;
  background: linear-gradient(180deg, #ffffff 0%, #fbecec 100%);
}

/* Cumulative Card */
.cumulative-card {
  border-color: #d9777b !important;
  background: linear-gradient(180deg, #fbecec 0%, #ffffff 100%);
}

/* Monthly Table */
.period-monthly-table thead th {
  font-size: 0.75rem !important;
  font-weight: 600 !important;
}
.period-monthly-table tfoot td {
  font-size: 0.8rem !important;
  border-top: 2px solid #e0e0e0;
}

/* Pivot Table */
.pivot-table-wrapper {
  overflow-x: auto;
  max-width: 100%;
}
.pivot-cross-table {
  white-space: nowrap;
}
.pivot-header-row-1 th {
  background: #f5f5f5 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.8rem !important;
  padding: 8px 10px !important;
}
.pivot-header-row-2 th {
  background: #fafafa !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.7rem !important;
  padding: 4px 10px !important;
}
.pivot-th-info {
  background: #fafafa !important;
}
.pivot-th-period {
  border-left: 2px solid #e0e0e0 !important;
}
.pivot-th-total {
  border-left: 3px solid #9e9e9e !important;
  background: #fbecec !important;
  color: #b3151b !important;
  font-weight: 700 !important;
}
.pivot-th-workforce {
  background: #fbecec !important;
  color: #b3151b !important;
  font-weight: 600 !important;
  border-left: 1px solid #d9777b !important;
}
.pivot-th-workforce--alert {
  background: #ffebee !important;
  color: #c62828 !important;
}

.pivot-period-1 {
  background: #f5f5f5 !important;
  color: #455a64 !important;
}
.pivot-period-2 {
  background: #e8f5e9 !important;
  color: #2e7d32 !important;
}
.pivot-period-3 {
  background: #fff4dc !important;
  color: #A86400 !important;
}
.pivot-period-4 {
  background: #fbecec !important;
  color: #c62828 !important;
}
.pivot-period-5 {
  background: #fbecec !important;
  color: #b3151b !important;
}
.pivot-period-1-sub {
  background: #f5f5f5 !important;
}
.pivot-period-2-sub {
  background: #e8f5e9 !important;
}
.pivot-period-3-sub {
  background: #fff4dc !important;
}
.pivot-period-4-sub {
  background: #fbecec !important;
}
.pivot-period-5-sub {
  background: #fbecec !important;
}
.pivot-total-sub {
  background: #fbecec !important;
}
.pivot-sub-th {
  font-size: 0.68rem !important;
  padding: 3px 8px !important;
  font-weight: 600 !important;
  border-left: 1px solid #e0e0e0 !important;
}

.pivot-data-row td {
  padding: 6px 10px !important;
  font-size: 0.78rem !important;
  border-bottom: 1px solid #f0f0f0 !important;
}
.pivot-row-alt {
  background: #fafafa;
}
.pivot-data-row:hover {
  background: #fbecec !important;
}
.pivot-cell-allocated {
  border-left: 2px solid #e0e0e0 !important;
}
.pivot-cell-disbursed {
  border-left: 1px solid #eeeeee !important;
}
.pivot-cell-remaining {
  border-left: 1px solid #eeeeee !important;
}
.pivot-cell-total {
  border-left: 3px solid #9e9e9e !important;
  background: rgba(237, 231, 246, 0.3) !important;
}
.pivot-footer-row {
  background: #f5f5f5 !important;
}
.pivot-footer-row td {
  padding: 10px !important;
  border-top: 3px solid #bdbdbd !important;
  font-size: 0.82rem !important;
}
</style>
